(function () {
  'use strict';

  const STORAGE_KEY = 'slimmr:wbq';
  const QUIZ_PLP_API_DEFAULT = 'https://demo-app-config.looprx.io/api/quiz-plp';
  const CREATE_WELLBEING_API_DEFAULT = 'https://demo-app-config.looprx.io/api/create-wellbeing';
  const WBQ_CATEGORY_ID_DEFAULT = '1';

  const STEP_CONFIG = [
    { id: 'WBQ-INTRO', step: 'intro' },
    { id: 'WBQ-LAST-DOSE-DATE', step: 'last-injection-date' },
    { id: 'WBQ-INJECTION-TWICE', step: 'injection-twice' },
    { id: 'WBQ-PEN-ISSUES', step: 'injection-pen-issues' },
    { id: 'WBQ-CURRENT-WEIGHT', step: 'current-weight' },
    { id: 'WBQ-SIDE-EFFECTS', step: 'side-effects' },
    { id: 'WBQ-MEDICATION-REGULARITY', step: 'medication-regularity' },
    { id: 'WBQ-PILL-ADHERENCE', step: 'pill-adherence' },
    { id: 'WBQ-PILL-TABLET-ADHERENCE', step: 'pill-tablet-adherence' },
    { id: 'WBQ-WEIGHT-LOSS-METHODS', step: 'weight-loss-methods' },
    { id: 'WBQ-HEALTH-CHANGES', step: 'health-changes' },
    { id: 'WBQ-MEDICATION-CHOICE', step: 'medication-choice' },
    { id: 'WBQ-NEXT-DOSE', step: 'next-dose-preference' },
    { id: 'WBQ-CONSULTATION-NEEDED', step: 'consultation-needed' },
    { id: 'WBQ-MEDICATION-SUPPLY', step: 'medication-supply' },
    { id: 'WBQ-DELIVERY-ACCESSORIES', step: 'delivery-accessories' },
    { id: 'WBQ-OPTIONAL-EXTRAS', step: 'optional-extras' },
    { id: 'WBQ-DELIVERY-DATE', step: 'delivery-date' }
  ];

  const DELIVERY_MIN_DAYS_FROM_TODAY = 2;
  const DELIVERY_DISABLED_WEEKDAYS = [0, 1];
  const DELIVERY_MAX_DAYS_FROM_TODAY = 14;
  const DELIVERY_DEFAULT_BLOCKED_RANGES = [
    { start: '2026-04-01', end: '2026-04-07' }
  ];
  const DELIVERY_DEFAULT_BLOCKED_DATES = [
    '2026-05-05',
    '2026-05-06',
    '2026-05-26',
    '2026-05-27',
    '2026-08-31'
  ];
  const DELIVERY_DATE_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;

  function compareDeliveryDateKeys(a, b) {
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
  }

  function isDeliveryDateKeyInRange(dateKey, startKey, endKey) {
    return compareDeliveryDateKeys(dateKey, startKey) >= 0 && compareDeliveryDateKeys(dateKey, endKey) <= 0;
  }

  function parseDeliveryDateKey(value) {
    const key = String(value || '').trim();
    return DELIVERY_DATE_KEY_RE.test(key) ? key : '';
  }

  function parseDeliveryBlockedDates(raw) {
    return String(raw || '')
      .split(',')
      .map(parseDeliveryDateKey)
      .filter(Boolean);
  }

  function parseDeliveryBlockedRanges(raw) {
    return String(raw || '')
      .split(',')
      .map((part) => {
        const bits = String(part || '').trim().split(':');
        if (bits.length !== 2) return null;
        const start = parseDeliveryDateKey(bits[0]);
        const end = parseDeliveryDateKey(bits[1]);
        if (!start || !end) return null;
        return { start, end };
      })
      .filter(Boolean);
  }

  function parseDeliveryPositiveInt(raw, fallback) {
    const value = Number(raw);
    if (!Number.isFinite(value) || value < 0) return fallback;
    return Math.floor(value);
  }

  function isPermanentDeliveryDate(dateKey, blockedDates, blockedRanges) {
    if (!dateKey) return false;

    const dates = blockedDates && blockedDates.length
      ? blockedDates
      : DELIVERY_DEFAULT_BLOCKED_DATES;
    if (dates.indexOf(dateKey) !== -1) {
      return true;
    }

    const ranges = blockedRanges && blockedRanges.length
      ? blockedRanges
      : DELIVERY_DEFAULT_BLOCKED_RANGES;
    for (let i = 0; i < ranges.length; i += 1) {
      const range = ranges[i];
      if (isDeliveryDateKeyInRange(dateKey, range.start, range.end)) {
        return true;
      }
    }

    return false;
  }

  function isCheckoutDisabled() {
    const params = new URLSearchParams(window.location.search);
    for (const [key, value] of params.entries()) {
      if (key.toLowerCase() === 'checkout' && String(value).toLowerCase() === 'false') {
        return true;
      }
    }
    return false;
  }

  function getAccessTokenFromCookie() {
    const match = document.cookie.match(/(?:^|;\s*)access_token=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  function yesNoLabel(value) {
    const raw = String(value || '').trim().toLowerCase();
    if (raw === 'yes') return 'Yes';
    if (raw === 'no') return 'No';
    return '';
  }

  function formatJoinedList(value) {
    if (Array.isArray(value)) {
      return value.map((item) => String(item || '').trim()).filter(Boolean).join(', ');
    }
    return String(value || '').trim();
  }

  function formatApiDateParts(day, month, year) {
    const d = Number(day);
    const m = Number(month);
    const y = Number(year);
    if (!d || !m || !y) return '';
    return `${y}-${m}-${d}`;
  }

  function getDaysSinceLastDose(data) {
    const day = Number(data?.lastDoseDay);
    const month = Number(data?.lastDoseMonth);
    const year = Number(data?.lastDoseYear);
    if (!day || !month || !year) return null;

    const lastDate = new Date(year, month - 1, day);
    if (Number.isNaN(lastDate.getTime())) return null;

    const today = new Date();
    lastDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    if (lastDate > today) return null;

    return Math.floor((today - lastDate) / (1000 * 60 * 60 * 24));
  }

  function formatNameDose(recommendedDose) {
    if (!recommendedDose || !recommendedDose.id) return '';
    const name = String(recommendedDose.name || '').trim();
    const variant = String(recommendedDose.variant || '').trim();
    if (name && variant) {
      const shortName = name
        .replace(/\s+once weekly injection$/i, '')
        .replace(/\s+daily pill$/i, ' Pill')
        .trim();
      return `${shortName} - ${variant}`;
    }
    return name || variant || '';
  }

  function formatEscalateDose(recommendedDose, direction) {
    if (!recommendedDose) return '';
    const variant = String(recommendedDose.variant || '').trim();
    if (!variant) return '';
    const dose = variant.match(/-?\d[\d.]*\s*mg/i)?.[0] || variant;
    if (direction === 'decrease') return dose.startsWith('-') ? dose : `-${dose.replace(/^\+/, '')}`;
    if (direction === 'increase') return dose.startsWith('+') ? dose : `+${dose.replace(/^-/, '')}`;
    if (direction === 'switch') return dose.startsWith('-') ? dose : `-${dose.replace(/^\+/, '')}`;
    return dose.replace(/^[+-]/, '');
  }

  const STEPS = STEP_CONFIG.map((entry) => entry.step);
  const TABLET_SKIP_STEPS = new Set([
    'injection-twice',
    'injection-pen-issues',
    'medication-regularity'
  ]);
  const LAST_DOSE_HEADING_INJECTION = 'What was the date of your most recent injection?';
  const LAST_DOSE_HEADING_PILL = 'What was the date you last took the medication?';
  const INJECTABLE_SKIP_STEPS = new Set(['pill-adherence', 'pill-tablet-adherence']);
  const STOP_TREATMENT_SKIP_STEPS = new Set([
    'medication-supply',
    'delivery-accessories',
    'optional-extras',
    'delivery-date'
  ]);
  const ASSESSMENT_PAGE_PATH = '/pages/assessment';
  const CONSULTATION_GAP_DAYS = 112;
  const LAST_DOSE_FIELDS = ['lastDoseDay', 'lastDoseMonth', 'lastDoseYear'];
  const TRAVEL_FROM_FIELDS = ['travelFromDay', 'travelFromMonth', 'travelFromYear'];
  const TRAVEL_TO_FIELDS = ['travelToDay', 'travelToMonth', 'travelToYear'];
  const TRAVEL_DATE_FIELDS = [...TRAVEL_FROM_FIELDS, ...TRAVEL_TO_FIELDS];
  const INJECTION_TWICE_KEY = 'injectedCurrentDoseTwice';
  const INJECTION_PEN_ISSUES_KEY = 'injectionPenIssues';
  const INJECTION_PEN_ISSUES_DETAIL_FIELD = 'injectionPenIssuesDetail';
  const SIDE_EFFECTS_KEY = 'sideEffects';
  const SIDE_EFFECTS_NONE_VALUE = 'None of the above';
  const SIDE_EFFECTS_OTHER_VALUE = 'Other';
  const SIDE_EFFECTS_OTHER_FIELD = 'sideEffectsOther';
  const SIDE_EFFECTS_IMPACT_KEY = 'sideEffectsImpact';
  const SIDE_EFFECTS_IMPACT_OTHER_VALUE = 'Other';
  const SIDE_EFFECTS_MANAGEMENT_FIELD = 'sideEffectsManagement';
  const MEDICATION_REGULARITY_KEY = 'medicationRegularity';
  const MEDICATION_REGULARITY_SKIPPED_TWO_OR_MORE = 'I have skipped 2 or more doses';
  const PILL_MISSED_DURATION_KEY = 'pillMissedDuration';
  const PILL_MISSED_NO_DOSES = "I didn't miss any doses.";
  const PILL_MISSED_NEVER_TWO_IN_ROW = 'I missed one or more doses overall, but never two in a row.';
  const PILL_MISSED_STOPPED_RESUMED = 'I stopped taking my medication for 2 days or longer, but later resumed treatment.';
  const PILL_MISSED_STOPPED_NOT_RESUMED = 'I stopped taking my medication and have not resumed treatment.';
  const PILL_STOPPED_VALUES = new Set([PILL_MISSED_STOPPED_RESUMED, PILL_MISSED_STOPPED_NOT_RESUMED]);
  const PILL_STOPPED_REASON_TRIGGER = PILL_MISSED_STOPPED_NOT_RESUMED;
  const PILL_STOPPED_REASON_FIELD = 'pillStoppedReason';
  const PILL_TABLET_ADHERENCE_KEY = 'pillTabletAdherence';
  const WEIGHT_LOSS_METHODS_KEY = 'weightLossMethods';
  const WEIGHT_LOSS_METHODS_NONE_VALUE = 'Nothing other than my medication';
  const WEIGHT_LOSS_METHODS_OTHER_VALUE = 'Other';
  const WEIGHT_LOSS_METHODS_OTHER_FIELD = 'weightLossMethodsOther';
  const HEALTH_CHANGES_KEY = 'healthChanges';
  const HEALTH_CHANGES_DETAIL_FIELD = 'healthChangesDetail';
  const MEDICATION_CHOICE_KEY = 'medicationChoice';
  const MEDICATION_NAME_MOUNJARO = 'Mounjaro once weekly injection';
  const MEDICATION_NAME_WEGOVY = 'Wegovy once weekly injection';
  const MEDICATION_NAME_WEGOVY_PILL = 'Wegovy daily pill';
  const MEDICATION_NAME_FOUNDAYO = 'Foundayo daily pill';
  const NEXT_DOSE_PREFERENCE_KEY = 'nextDosePreference';
  const NEXT_DOSE_INCREASE = 'I want to increase my dose';
  const NEXT_DOSE_SAME = 'I want to stay on the same dose';
  const NEXT_DOSE_DECREASE = 'I want to decrease my dose';
  const NEXT_DOSE_DISCUSS = 'I want to discuss my next dose with my prescriber';
  const NEXT_DOSE_STOP = 'I want to stop treatment';
  const NEXT_DOSE_DECREASE_REASON_KEY = 'nextDoseDecreaseReason';
  const NEXT_DOSE_DECREASE_SIDE_EFFECTS = 'I am experiencing side effects on my current dose';
  const NEXT_DOSE_DECREASE_GOAL_WEIGHT = 'I have reached my goal weight and am now moving to a maintenance dose';
  const NEXT_DOSE_DECREASE_NOTES_FIELD = 'nextDoseDecreaseNotes';
  const NEXT_DOSE_PRESCRIBER_NOTES_FIELD = 'nextDosePrescriberNotes';
  const NEXT_DOSE_STOP_REASON_FIELD = 'nextDoseStopReason';
  const MEDICATION_SUPPLY_KEY = 'medicationSupply';
  const MEDICATION_SUPPLY_NONE = 'No — continue';
  const MEDICATION_SUPPLY_TRAVEL = 'Travelling for over 3 weeks';
  const TRAVEL_PROOF_UPLOAD_KEY = 'travelProofUpload';
  const TRAVEL_SCHEDULE_REVIEW_KEY = 'travelScheduleReviewAcknowledged';
  const SHARPS_BIN_KEY = 'sharpsBin';
  const EXTRA_NEEDLES_KEY = 'extraNeedles';
  const PENCYCLE_BOX_KEY = 'pencycleBox';
  const EXTRA_NEEDLES_YES = 'Yes — add extra needles';
  const PENCYCLE_BOX_YES = 'Yes — add a Pencycle box';
  const WBQ_DETAIL_TEXTAREA_FIELDS = new Set([
    INJECTION_PEN_ISSUES_DETAIL_FIELD,
    SIDE_EFFECTS_OTHER_FIELD,
    SIDE_EFFECTS_MANAGEMENT_FIELD,
    WEIGHT_LOSS_METHODS_OTHER_FIELD,
    HEALTH_CHANGES_DETAIL_FIELD,
    PILL_STOPPED_REASON_FIELD,
    NEXT_DOSE_DECREASE_NOTES_FIELD,
    NEXT_DOSE_PRESCRIBER_NOTES_FIELD,
    NEXT_DOSE_STOP_REASON_FIELD
  ]);

  const BODY_MEASUREMENT_RANGES = {
    WEIGHT_KG: { min: 30, max: 300, message: 'Please enter a weight between 30 kg and 300 kg.' },
    WEIGHT_IMPERIAL_MESSAGE: 'Please enter a weight between 4st 10lb and 47st 3lb.',
    POUNDS_MAX: 14,
    NUMBERS_ONLY: 'Please enter numbers only.'
  };
  const CURRENT_WEIGHT_INTEGER_FIELDS = new Set(['currentStones', 'currentPounds']);
  const CURRENT_WEIGHT_DECIMAL_FIELDS = new Set(['currentKg']);
  const CURRENT_WEIGHT_FIELDS = ['currentStones', 'currentPounds', 'currentKg'];

  function parseNumericField(value) {
    const str = String(value ?? '').trim().replace(',', '.');
    if (str === '') return { empty: true, valid: false, value: NaN };
    const num = Number(str);
    return { empty: false, valid: Number.isFinite(num), value: num };
  }

  function sanitizeIntegerInput(value) {
    return String(value || '').replace(/\D/g, '');
  }

  function sanitizeDecimalInput(value) {
    let str = String(value || '').replace(/[^\d.,]/g, '').replace(',', '.');
    const dotIndex = str.indexOf('.');
    if (dotIndex === -1) return str;

    const whole = str.slice(0, dotIndex);
    const fraction = str.slice(dotIndex + 1).replace(/\./g, '');
    return `${whole}.${fraction}`;
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/"/g, '&quot;');
  }

  function simplifyMedicationName(name) {
    const str = String(name || '').trim();
    if (!str) return '';
    const match = str.match(/^([^(]+?)(?:\s*\(|$)/);
    return match ? match[1].trim() : str;
  }

  function resolveForceMedicationKey() {
    const raw = getUrlParam('forceMed') || getUrlParam('forcemed') || getUrlParam('med');
    const value = String(raw || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
    if (!value) return '';
    if (['foundayo', 'orforglipron'].includes(value)) return 'foundayo';
    if (['wegovypill', 'pill', 'tablet', 'dailypill'].includes(value)) return 'wegovy_pill';
    if (['mounjaro', 'tirzepatide'].includes(value)) return 'mounjaro';
    if (['wegovy', 'semaglutide', 'wegovyinjection'].includes(value)) return 'wegovy';
    if (value === 'ozempic') return 'ozempic';
    return '';
  }

  function getForcedProductLabel(medicationKey) {
    if (medicationKey === 'foundayo') return MEDICATION_NAME_FOUNDAYO;
    if (medicationKey === 'wegovy_pill') return MEDICATION_NAME_WEGOVY_PILL;
    if (medicationKey === 'mounjaro') return MEDICATION_NAME_MOUNJARO;
    if (medicationKey === 'wegovy' || medicationKey === 'ozempic') return MEDICATION_NAME_WEGOVY;
    return '';
  }

  function resolveMedicationKey(productPurchased, lastProductName, forceKey) {
    if (forceKey) return forceKey;
    const combined = [productPurchased, lastProductName].map((s) => String(s || '').toLowerCase()).join(' ');
    if (combined.includes('foundayo') || combined.includes('orforglipron')) return 'foundayo';
    if (combined.includes('wegovy') && (combined.includes('pill') || combined.includes('tablet'))) return 'wegovy_pill';
    if (combined.includes('pill') || combined.includes('tablet')) return 'wegovy_pill';
    if (combined.includes('mounjaro') || combined.includes('tirzepatide')) return 'mounjaro';
    if (combined.includes('ozempic')) return 'ozempic';
    if (combined.includes('wegovy') || combined.includes('semaglutide')) return 'wegovy';
    return '';
  }

  function isFoundayoMedication(productPurchased, lastProductName, forceKey) {
    return resolveMedicationKey(productPurchased, lastProductName, forceKey) === 'foundayo';
  }

  function isWegovyPillMedication(productPurchased, lastProductName, forceKey) {
    return resolveMedicationKey(productPurchased, lastProductName, forceKey) === 'wegovy_pill';
  }

  function isTabletMedication(productPurchased, lastProductName, forceKey) {
    const key = resolveMedicationKey(productPurchased, lastProductName, forceKey);
    return key === 'wegovy_pill' || key === 'foundayo';
  }

  function getMedicationDisplayName(productPurchased, lastProductName) {
    const purchased = String(productPurchased || '').trim();
    if (purchased) return simplifyMedicationName(purchased);

    const lastName = String(lastProductName || '').trim();
    return simplifyMedicationName(lastName);
  }

  function getCanonicalMedicationName(medicationKey) {
    if (medicationKey === 'mounjaro') return MEDICATION_NAME_MOUNJARO;
    if (medicationKey === 'wegovy' || medicationKey === 'ozempic') return MEDICATION_NAME_WEGOVY;
    if (medicationKey === 'foundayo') return MEDICATION_NAME_FOUNDAYO;
    if (medicationKey === 'wegovy_pill') return MEDICATION_NAME_WEGOVY_PILL;
    return '';
  }

  function getMedicationChoiceOptions(data, forceKey) {
    const medicationKey = resolveMedicationKey(data.productPurchased, data.lastProductName, forceKey);
    const currentName = getCanonicalMedicationName(medicationKey);
    const options = [];

    if (medicationKey === 'mounjaro') {
      options.push(`Stay on ${MEDICATION_NAME_MOUNJARO}`);
      options.push(`Request a switch to ${MEDICATION_NAME_WEGOVY}`);
      options.push(`Switch to ${MEDICATION_NAME_WEGOVY_PILL}`);
      options.push(`Switch to ${MEDICATION_NAME_FOUNDAYO}`);
    } else if (medicationKey === 'wegovy' || medicationKey === 'ozempic') {
      options.push(`Stay on ${MEDICATION_NAME_WEGOVY}`);
      options.push(`Request a switch to ${MEDICATION_NAME_MOUNJARO}`);
      options.push(`Switch to ${MEDICATION_NAME_WEGOVY_PILL}`);
      options.push(`Switch to ${MEDICATION_NAME_FOUNDAYO}`);
    } else if (medicationKey === 'wegovy_pill') {
      options.push(`Stay on ${MEDICATION_NAME_WEGOVY_PILL}`);
      options.push(`Request a switch to ${MEDICATION_NAME_MOUNJARO}`);
      options.push(`Request a switch to ${MEDICATION_NAME_WEGOVY}`);
      options.push(`Switch to ${MEDICATION_NAME_FOUNDAYO}`);
    } else if (medicationKey === 'foundayo') {
      options.push(`Stay on ${MEDICATION_NAME_FOUNDAYO}`);
      options.push(`Request a switch to ${MEDICATION_NAME_MOUNJARO}`);
      options.push(`Request a switch to ${MEDICATION_NAME_WEGOVY}`);
      options.push(`Switch to ${MEDICATION_NAME_WEGOVY_PILL}`);
    } else if (currentName) {
      options.push(`Stay on ${currentName}`);
      options.push(`Request a switch to ${MEDICATION_NAME_WEGOVY}`);
      options.push(`Request a switch to ${MEDICATION_NAME_MOUNJARO}`);
      options.push(`Switch to ${MEDICATION_NAME_WEGOVY_PILL}`);
      options.push(`Switch to ${MEDICATION_NAME_FOUNDAYO}`);
    } else {
      options.push(`Stay on ${MEDICATION_NAME_MOUNJARO}`);
      options.push(`Request a switch to ${MEDICATION_NAME_WEGOVY}`);
      options.push(`Switch to ${MEDICATION_NAME_WEGOVY_PILL}`);
      options.push(`Switch to ${MEDICATION_NAME_FOUNDAYO}`);
    }

    return options;
  }

  const PRESCRIBER_VARIANT = {
    id: '52825891045715',
    name: 'Prescriber',
    variant: 'Discuss with prescriber'
  };

  const WEGOVY_72_VARIANT = {
    name: '7.2mg',
    label: '7.2mg',
    title: '7.2mg',
    shopify_variant_id: '53931903058259',
    active: false
  };

  // Theme fallback when quiz-plp does not yet return a Foundayo product family.
  const FOUNDAYO_08_VARIANT = {
    name: '0.8mg',
    label: '0.8mg',
    title: '0.8mg',
    shopify_variant_id: '54745598886227',
    active: false
  };

  const WEGOVY_INJECTION_TO_WEGOVY_PILL = {
    0.25: 4,
    0.5: 4,
    1: 4,
    1.7: 9,
    2.4: 25,
    7.2: 25
  };

  const MOUNJARO_TO_WEGOVY_PILL = {
    2.5: 4,
    5: 4,
    7.5: 9,
    10: 9,
    12.5: 25,
    15: 25
  };

  const WEGOVY_PILL_DOSE_MGS = [1.5, 4, 9, 25];
  const WEGOVY_PILL_ON_LABEL_START_MG = 1.5;
  const WEGOVY_PILL_DISCLAIMER_TEXT = [
    'You have selected an enhanced switch dose. This prescribing approach is off-label, meaning it differs from the licensed starting regimen for Wegovy tablets.',
    'Your prescriber will assess whether this is appropriate for you based on your current treatment, tolerance and medical history. An enhanced switch cannot be guaranteed.',
    'I understand that I am requesting an off-label switch and that the final prescribing decision rests with the clinician.'
  ].join('\n\n');

  const FOUNDAYO_DOSE_MGS = [0.8, 2.5, 5.5, 9, 14.5, 17.2];
  const FOUNDAYO_ON_LABEL_START_MG = 0.8;
  const FOUNDAYO_DISCLAIMER_TEXT = [
    'You have selected an enhanced switch dose. This prescribing approach is off-label, meaning it differs from the licensed starting regimen for Foundayo tablets.',
    'Your prescriber will assess whether this is appropriate for you based on your current treatment, tolerance and medical history. An enhanced switch cannot be guaranteed.',
    'I understand that I am requesting an off-label switch and that the final prescribing decision rests with the clinician.'
  ].join('\n\n');

  const WEGOVY_INJECTION_TO_FOUNDAYO = {
    0.25: 2.5,
    0.5: 2.5,
    1: 2.5,
    1.7: 9,
    2.4: 17.2,
    7.2: 17.2
  };

  const MOUNJARO_TO_FOUNDAYO = {
    2.5: 2.5,
    5: 2.5,
    7.5: 9,
    10: 9,
    12.5: 17.2,
    15: 17.2
  };

  function getMappedInjectableToFoundayoMg() {
    // Foundayo has no prior treatment history yet — always on-label start.
    return FOUNDAYO_ON_LABEL_START_MG;
  }

  function getFoundayoSwitchDoseMgs() {
    // No enhanced-switch ladder until Foundayo treatment history exists.
    return [FOUNDAYO_ON_LABEL_START_MG];
  }

  function isEnhancedFoundayoDoseMg(doseMg) {
    const value = Number(doseMg);
    return Number.isFinite(value) && value > FOUNDAYO_ON_LABEL_START_MG;
  }

  function getMappedInjectableToWegovyPillMg(current, previousProduct) {
    if (previousProduct === 'Wegovy') return WEGOVY_INJECTION_TO_WEGOVY_PILL[current] ?? WEGOVY_PILL_ON_LABEL_START_MG;
    if (previousProduct === 'Mounjaro') return MOUNJARO_TO_WEGOVY_PILL[current] ?? WEGOVY_PILL_ON_LABEL_START_MG;
    return WEGOVY_PILL_ON_LABEL_START_MG;
  }

  function getWegovyPillSwitchDoseMgs(mappedMg, gapGreaterThan27) {
    if (gapGreaterThan27) return [WEGOVY_PILL_ON_LABEL_START_MG];
    const mapped = Number(mappedMg);
    const ceiling = Number.isFinite(mapped) ? mapped : WEGOVY_PILL_ON_LABEL_START_MG;
    return WEGOVY_PILL_DOSE_MGS.filter((dose) => dose <= ceiling);
  }

  function isEnhancedWegovyPillDoseMg(doseMg) {
    const value = Number(doseMg);
    return Number.isFinite(value) && value > WEGOVY_PILL_ON_LABEL_START_MG;
  }

  const MOUNJARO_TO_WEGOVY = new Map([
    [2.5, 0.25],
    [5, 0.25],
    [7.5, 0.5],
    [10, 0.5],
    [12.5, 1.0],
    [15, 1.0]
  ]);

  function normalizeProductFamily(raw) {
    const value = String(raw || '').toLowerCase();
    if (!value) return null;
    if (value.includes('foundayo') || value.includes('orforglipron')) return 'Foundayo';
    if (value.includes('wegovy') && (value.includes('pill') || value.includes('tablet'))) return 'WegovyPill';
    if (value.includes('pill') || value.includes('tablet')) return 'WegovyPill';
    if (value.includes('mounjaro') || value.includes('tirzepatide')) return 'Mounjaro';
    if (value.includes('wegovy') || value.includes('semaglutide')) return 'Wegovy';
    return null;
  }

  function getDoseLabel(item) {
    if (!item) return '';
    if (typeof item === 'string' || typeof item === 'number') return String(item);
    return item.title ?? item.label ?? item.name ?? '';
  }

  function toDoseNumber(v) {
    if (typeof v === 'number') return v;
    if (typeof v === 'string') {
      const m = v.match(/[\d.]+/);
      return m ? parseFloat(m[0]) : NaN;
    }
    return NaN;
  }

  function normDose(n) {
    if (typeof n !== 'number' || Number.isNaN(n)) return NaN;
    return Math.round(n * 100) / 100;
  }

  // quiz-plp sometimes returns last_product_dose "Default Title"; parse mg from product name.
  function formatDoseLabelFromMg(mg) {
    if (typeof mg !== 'number' || Number.isNaN(mg)) return '';
    const rounded = normDose(mg);
    if (Number.isNaN(rounded)) return '';
    return Number.isInteger(rounded) ? `${rounded}mg` : `${rounded}mg`;
  }

  function resolveLastProductDose(dose, productPurchased, lastProductName) {
    const rawDose = String(dose || '').trim();
    if (rawDose && !Number.isNaN(toDoseNumber(rawDose))) {
      return rawDose;
    }

    const fromProduct = String(productPurchased || '').trim() || String(lastProductName || '').trim();
    const mg = normDose(toDoseNumber(fromProduct));
    if (!Number.isNaN(mg)) {
      return formatDoseLabelFromMg(mg);
    }

    return rawDose;
  }

  function findApiProduct(products, family) {
    if (!Array.isArray(products) || !family) return null;
    return products.find((p) => normalizeProductFamily(p?.title || p?.name) === family) || null;
  }

  function getApiDoseLadder(products, family) {
    const apiProduct = findApiProduct(products, family);
    let dosesRaw = Array.isArray(apiProduct?.variants)
      ? apiProduct.variants
      : (Array.isArray(apiProduct?.doses) ? apiProduct.doses : []);

    if (family === 'Wegovy') {
      const hasWegovy72 = dosesRaw.some((v) => normDose(toDoseNumber(getDoseLabel(v))) === 7.2);
      if (!hasWegovy72) dosesRaw = [...dosesRaw, WEGOVY_72_VARIANT];
    }

    return dosesRaw
      .map((v) => normDose(toDoseNumber(getDoseLabel(v))))
      .filter((value) => !Number.isNaN(value))
      .sort((a, b) => a - b);
  }

  function getCurrentDoseBounds(data) {
    const previousName = data?.productPurchased || data?.lastProductName || '';
    const family = normalizeProductFamily(previousName);
    const current = normDose(toDoseNumber(data?.lastProductDose || previousName));
    const ladder = getApiDoseLadder(data?.quizPlpProducts, family);

    if (!family || !ladder.length || Number.isNaN(current)) {
      return { atMax: false, atMin: false, current: NaN, min: NaN, max: NaN };
    }

    const min = ladder[0];
    const max = ladder[ladder.length - 1];
    return {
      atMax: current >= max,
      atMin: current <= min,
      current,
      min,
      max
    };
  }

  function isMedicationSwitchChoice(medicationChoice) {
    return /switch|request a switch/.test(String(medicationChoice || '').toLowerCase());
  }

  function resolveTargetMedicationChoice(medicationChoice, previousFamily) {
    const choice = String(medicationChoice || '').toLowerCase();
    if (!choice) return previousFamily;

    const isSwitch = isMedicationSwitchChoice(choice);
    if (!isSwitch && /stay on|continue/.test(choice)) return previousFamily;

    if (choice.includes('foundayo')) return 'Foundayo';
    if (choice.includes('daily pill') || (choice.includes('pill') && !choice.includes('injection') && !choice.includes('foundayo'))) {
      return 'WegovyPill';
    }
    if (choice.includes('mounjaro')) return 'Mounjaro';
    if (choice.includes('wegovy')) return 'Wegovy';
    return previousFamily;
  }

  /** Injection-only Wegovy accessories (needles + Pencycle). Not for pill/tablet. */
  function isWegovyInjectionAccessoriesFamily(family) {
    return family === 'Wegovy';
  }

  /** Sharps bin for injectables only (Wegovy injection + Mounjaro), not pill/tablet. */
  function isInjectableAccessoriesFamily(family) {
    return family === 'Wegovy' || family === 'Mounjaro';
  }

  function yesNoFromSelector(value) {
    const str = String(value || '').trim();
    if (!str) return '';
    return /^yes/i.test(str) ? 'Yes' : 'No';
  }

  function resolveDoseDirection(nextDosePreference, medicationChoice, previousFamily, targetFamily) {
    const preference = String(nextDosePreference || '').toLowerCase();
    const choice = String(medicationChoice || '').toLowerCase();

    if (isMedicationSwitchChoice(choice)) return 'switch';
    if (preference.includes('prescriber') || preference.includes('discuss')) return 'prescriber';
    if (preference.includes('stop')) return 'stop';

    if (preference.includes('increase')) return 'increase';
    if (preference.includes('decrease')) return 'decrease';
    if (preference.includes('same') || preference.includes('stay')) return 'same';

    if (previousFamily && targetFamily && previousFamily !== targetFamily) return 'switch';
    return 'same';
  }

  function recommendDose(args) {
    const dosesRaw = args.doses ?? [];
    const currentRaw = args.currentDose;
    const productRaw = String(args.productName || '');
    const previousRaw = String(args.previousProduct || '');
    const directionRaw = String(args.direction || '').toLowerCase().trim();

    const dd = Number(args.dd);
    const mm = Number(args.mm);
    const yy = Number(args.yy);

    if (directionRaw.includes('prescriber') || directionRaw === 'discuss') {
      return { ...PRESCRIBER_VARIANT };
    }

    if (directionRaw.includes('stop')) return null;

    const product = normalizeProductFamily(productRaw);
    const previousProduct = normalizeProductFamily(previousRaw);
    if (!product) return null;

    let dir =
      directionRaw.includes('switch') ? 'switch' :
      directionRaw.includes('increase') ? 'increase' :
      directionRaw.includes('decrease') ? 'decrease' :
      directionRaw.includes('same') || directionRaw.includes('stay') ? 'same' :
      null;

    if (!dir) {
      if (previousProduct && previousProduct !== product) dir = 'switch';
      else dir = 'same';
    }

    const toReturn = (variantObj, productName) => ({
      id: String(variantObj.id),
      name: productName || (
        product === 'Foundayo' ? MEDICATION_NAME_FOUNDAYO :
        product === 'WegovyPill' ? MEDICATION_NAME_WEGOVY_PILL :
        product === 'Mounjaro' ? 'Mounjaro' :
        'Wegovy'
      ),
      variant: variantObj.label
    });

    let daysSince = 0;
    if (!Number.isNaN(dd) && !Number.isNaN(mm) && !Number.isNaN(yy) && dd && mm && yy) {
      const lastDate = new Date(yy, mm - 1, dd);
      const today = new Date();
      if (!Number.isNaN(lastDate.getTime()) && lastDate <= today) {
        daysSince = Math.floor((today - lastDate) / (1000 * 60 * 60 * 24));
      }
    }
    const gapTooBig = daysSince > 27;

    const hasWegovy72 = dosesRaw.some((v) => normDose(toDoseNumber(getDoseLabel(v))) === 7.2);
    let updatedDosesRaw =
      product === 'Wegovy' && !hasWegovy72
        ? [...dosesRaw, WEGOVY_72_VARIANT]
        : dosesRaw;

    // Foundayo may be missing from quiz-plp — inject on-label starter so checkout has a variant.
    if (product === 'Foundayo' && !updatedDosesRaw.length) {
      updatedDosesRaw = [FOUNDAYO_08_VARIANT];
    }

    const doses = updatedDosesRaw
      .map((v) => {
        const value = normDose(toDoseNumber(getDoseLabel(v)));
        return {
          value,
          id: v.shopify_variant_id ?? v.id,
          label: getDoseLabel(v)
        };
      })
      .filter((d) => !Number.isNaN(d.value) && d.id)
      .sort((a, b) => a.value - b.value);

    if (!doses.length) return null;

    const baseVariant = doses[0];
    const highestVariant = doses[doses.length - 1];
    const displayName =
      product === 'Foundayo' ? MEDICATION_NAME_FOUNDAYO :
      product === 'WegovyPill' ? MEDICATION_NAME_WEGOVY_PILL :
      product === 'Mounjaro' ? 'Mounjaro' :
      'Wegovy';

    const current = normDose(toDoseNumber(currentRaw));

    // Foundayo has no treatment history yet — every patient gets 0.8mg only.
    if (product === 'Foundayo') {
      const starter =
        doses.find((d) => d.value === FOUNDAYO_ON_LABEL_START_MG)
        || {
          value: FOUNDAYO_ON_LABEL_START_MG,
          id: FOUNDAYO_08_VARIANT.shopify_variant_id,
          label: FOUNDAYO_08_VARIANT.label
        };
      return toReturn(starter, displayName);
    }

    // Switch maps always win; do not apply gap > 27 rule for switch cases.
    if (dir === 'switch' && previousProduct && previousProduct !== product) {
      if (Number.isNaN(current)) return toReturn(baseVariant, displayName);

      if (previousProduct === 'Wegovy' && product === 'Mounjaro') {
        // Default 2.5mg; Wegovy 7.2mg → Mounjaro 5mg (subject to prescriber review).
        const mappedDose = normDose(current === 7.2 ? 5 : 2.5);
        const match = doses.find((d) => d.value === mappedDose) || baseVariant;
        return toReturn(match, displayName);
      }

      if (previousProduct === 'Mounjaro' && product === 'Wegovy') {
        const mappedDose = normDose(MOUNJARO_TO_WEGOVY.get(current));
        const match = doses.find((d) => d.value === mappedDose) || baseVariant;
        return toReturn(match, displayName);
      }

      if (product === 'WegovyPill') {
        let mappedDose = 1.5;
        if (previousProduct === 'Wegovy') {
          mappedDose = WEGOVY_INJECTION_TO_WEGOVY_PILL[current] ?? 1.5;
        } else if (previousProduct === 'Mounjaro') {
          mappedDose = MOUNJARO_TO_WEGOVY_PILL[current] ?? 1.5;
        } else if (previousProduct === 'Foundayo') {
          mappedDose = 1.5;
        }
        mappedDose = normDose(mappedDose);

        const selected = normDose(toDoseNumber(args.selectedDoseMg));
        if (!Number.isNaN(selected) && selected <= mappedDose) {
          const selectedMatch = doses.find((d) => d.value === selected);
          if (selectedMatch) return toReturn(selectedMatch, displayName);
        }

        const match = doses.find((d) => d.value === mappedDose) || baseVariant;
        return toReturn(match, displayName);
      }

      if (previousProduct === 'WegovyPill' || previousProduct === 'Foundayo') {
        return toReturn(baseVariant, displayName);
      }

      return toReturn(baseVariant, displayName);
    }

    if (gapTooBig) return toReturn(baseVariant, displayName);

    if (Number.isNaN(current)) return null;

    if (dir === 'same') {
      let best = doses[0];
      let diff = Math.abs(best.value - current);
      for (let i = 1; i < doses.length; i += 1) {
        const d = Math.abs(doses[i].value - current);
        if (d < diff) {
          diff = d;
          best = doses[i];
        }
      }
      return toReturn(best, displayName);
    }

    if (dir === 'increase') {
      const next = doses.find((d) => d.value > current) || highestVariant;
      return toReturn(next, displayName);
    }

    if (dir === 'decrease') {
      const prev = [...doses].reverse().find((d) => d.value < current) || baseVariant;
      return toReturn(prev, displayName);
    }

    return toReturn(baseVariant, displayName);
  }

  function clearWbqSession() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (_) {}
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
  }

  function getUrlParam(name) {
    return new URLSearchParams(window.location.search).get(name);
  }

  function normalizeEmailParam(raw) {
    let value = String(raw || '').trim();
    if (!value) return '';
    try {
      while (value.includes('%')) {
        const decoded = decodeURIComponent(value);
        if (decoded === value) break;
        value = decoded;
      }
    } catch (_) {}
    return value.trim().toLowerCase();
  }

  function parseQuizPlpResponse(payload) {
    if (!payload || typeof payload !== 'object') return null;
    const layer = payload.data;
    if (layer && typeof layer === 'object') {
      if (layer.last_product_name != null || layer.product_purchased != null || layer.products) {
        return layer;
      }
      if (layer.data && typeof layer.data === 'object') return layer.data;
    }
    if (payload.last_product_name != null || payload.product_purchased != null) return payload;
    return null;
  }

  function formatLastTreatmentLabel(productName, dose) {
    const name = String(productName || '').trim();
    const doseLabel = String(dose || '').trim();
    if (!name && !doseLabel) return '';
    if (name && (!doseLabel || name.includes(doseLabel))) return `Your last treatment: ${name}`;
    if (!name && doseLabel) return `Your last treatment: ${doseLabel}`;
    return `Your last treatment: ${name} - ${doseLabel}`;
  }

  function extractLastTreatment(patient) {
    if (!patient) return { productName: '', dose: '' };
    const purchased = String(patient.product_purchased || '').trim();
    const productName = String(patient.last_product_name || '').trim();
    const dose = String(patient.last_product_dose || '').trim();
    if (purchased) return { productName: purchased, dose: '' };
    return { productName, dose };
  }

  function parseLastInjectionDate(dayStr, monthStr, yearStr) {
    const dayRaw = String(dayStr || '').trim();
    const monthRaw = String(monthStr || '').trim();
    const yearRaw = String(yearStr || '').trim();
    const allFilled = !!(dayRaw && monthRaw && yearRaw);

    if (!dayRaw) return { valid: false, field: 'lastDoseDay', message: 'Please enter a day.', banner: false };
    if (!monthRaw) return { valid: false, field: 'lastDoseMonth', message: 'Please enter a month.', banner: false };
    if (!yearRaw) return { valid: false, field: 'lastDoseYear', message: 'Please enter a year.', banner: false };

    if (!/^\d{1,2}$/.test(dayRaw)) {
      return { valid: false, field: 'lastDoseDay', message: 'Day must be 1 or 2 digits.', banner: allFilled };
    }
    if (!/^\d{1,2}$/.test(monthRaw)) {
      return { valid: false, field: 'lastDoseMonth', message: 'Month must be 1 or 2 digits.', banner: allFilled };
    }
    if (!/^\d{4}$/.test(yearRaw)) {
      return { valid: false, field: 'lastDoseYear', message: 'Please enter a 4-digit year.', banner: allFilled };
    }

    const day = parseInt(dayRaw, 10);
    const month = parseInt(monthRaw, 10);
    const year = parseInt(yearRaw, 10);

    if (day < 1 || day > 31) {
      return { valid: false, field: 'lastDoseDay', message: 'Day must be between 1 and 31.', banner: true };
    }
    if (month < 1 || month > 12) {
      return { valid: false, field: 'lastDoseMonth', message: 'Month must be between 1 and 12.', banner: true };
    }

    const currentYear = new Date().getFullYear();
    if (year < 2000 || year > currentYear) {
      return {
        valid: false,
        field: 'lastDoseYear',
        message: `Year must be between 2000 and ${currentYear}.`,
        banner: true
      };
    }

    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return { valid: false, field: 'lastDoseDay', message: 'Please enter a valid date.', banner: true };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date > today) {
      return { valid: false, field: 'lastDoseDay', message: 'Date cannot be in the future.', banner: true };
    }

    const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return { valid: true, day, month, year, iso };
  }

  function isTripOverThreeWeeks(args) {
    const startDate = new Date(
      Number(args.startYear),
      Number(args.startMonth) - 1,
      Number(args.startDay)
    );
    const endDate = new Date(
      Number(args.endYear),
      Number(args.endMonth) - 1,
      Number(args.endDay)
    );

    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      return false;
    }

    if (endDate < startDate) return false;

    const msPerDay = 1000 * 60 * 60 * 24;
    const diffDays = Math.floor((endDate - startDate) / msPerDay) + 1;
    return diffDays >= 21;
  }

  function parseTravelDate(dayStr, monthStr, yearStr, fieldPrefix) {
    const dayField = fieldPrefix + 'Day';
    const monthField = fieldPrefix + 'Month';
    const yearField = fieldPrefix + 'Year';
    const dayRaw = String(dayStr || '').trim();
    const monthRaw = String(monthStr || '').trim();
    const yearRaw = String(yearStr || '').trim();

    if (!dayRaw) return { valid: false, field: dayField, message: 'Please enter a day.' };
    if (!monthRaw) return { valid: false, field: monthField, message: 'Please enter a month.' };
    if (!yearRaw) return { valid: false, field: yearField, message: 'Please enter a year.' };

    if (!/^\d{1,2}$/.test(dayRaw)) {
      return { valid: false, field: dayField, message: 'Day must be 1 or 2 digits.' };
    }
    if (!/^\d{1,2}$/.test(monthRaw)) {
      return { valid: false, field: monthField, message: 'Month must be 1 or 2 digits.' };
    }
    if (!/^\d{4}$/.test(yearRaw)) {
      return { valid: false, field: yearField, message: 'Please enter a 4-digit year.' };
    }

    const day = parseInt(dayRaw, 10);
    const month = parseInt(monthRaw, 10);
    const year = parseInt(yearRaw, 10);
    const currentYear = new Date().getFullYear();

    if (day < 1 || day > 31) {
      return { valid: false, field: dayField, message: 'Day must be between 1 and 31.' };
    }
    if (month < 1 || month > 12) {
      return { valid: false, field: monthField, message: 'Month must be between 1 and 12.' };
    }
    if (year < currentYear - 1 || year > currentYear + 5) {
      return {
        valid: false,
        field: yearField,
        message: `Year must be between ${currentYear - 1} and ${currentYear + 5}.`
      };
    }

    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return { valid: false, field: dayField, message: 'Please enter a valid date.' };
    }

    const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return { valid: true, day, month, year, iso, date };
  }

  class WbqForm {
    constructor(root) {
      this.root = root;
      this.formName = root.dataset.formName || 'WBQ';
      this.categoryId = root.dataset.categoryId || WBQ_CATEGORY_ID_DEFAULT;
      this.quizPlpApiUrl = root.dataset.quizPlpApiUrl || QUIZ_PLP_API_DEFAULT;
      this.createWellbeingApiUrl = root.dataset.createWellbeingApiUrl || CREATE_WELLBEING_API_DEFAULT;
      this.apiTenant = root.dataset.gpApiTenant || 'slimmr-K9FQ7A2D';
      this.forceMedKey = resolveForceMedicationKey();
      this.currentStep = 0;
      this.travelUploadcareWidget = null;
      this.deliveryCalendarView = null;
      const blockedDates = parseDeliveryBlockedDates(root.dataset.deliveryBlockedDates);
      const blockedRanges = parseDeliveryBlockedRanges(root.dataset.deliveryBlockedRanges);
      this.deliveryBlockedDates = blockedDates.length
        ? blockedDates
        : DELIVERY_DEFAULT_BLOCKED_DATES.slice();
      this.deliveryBlockedRanges = blockedRanges.length
        ? blockedRanges
        : DELIVERY_DEFAULT_BLOCKED_RANGES.map((range) => ({ ...range }));
      this.deliveryMinDays = parseDeliveryPositiveInt(
        root.dataset.deliveryMinDays,
        DELIVERY_MIN_DAYS_FROM_TODAY
      );
      this.deliveryMaxDays = parseDeliveryPositiveInt(
        root.dataset.deliveryMaxDays,
        DELIVERY_MAX_DAYS_FROM_TODAY
      );
      this.data = this.loadData();
      this.init();
    }

    loadData() {
      try {
        const saved = sessionStorage.getItem(STORAGE_KEY);
        return saved ? JSON.parse(saved) : {};
      } catch (_) {
        return {};
      }
    }

    saveData() {
      try {
        this.data._stepIndex = this.currentStep;
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.data));
      } catch (_) {}
    }

    init() {
      this.stepsEl = this.root.querySelector('[data-wbq-steps]');
      this.progressFill = this.root.querySelector('[data-wbq-progress]');
      this.navEl = this.root.querySelector('[data-wbq-nav]');

      this.bindEvents();
      this.applyEmailFromUrlParams();
      this.applyForceMedicationFromUrlParams();
      this.initTravelProofUpload();
      this.restoreLastDoseFields();
      this.applyCurrentMonthYearPrefill();
      this.restoreMeasurementFields();
      this.restoreSelectorGroups();
      this.loadLastTreatment();

      const savedIndex = Number(this.data._stepIndex);
      let startIndex = Number.isInteger(savedIndex) && savedIndex >= 0 && savedIndex < STEPS.length
        ? savedIndex
        : 0;
      startIndex = this.resolveAvailableStepIndex(startIndex, 1);

      this.goToStep(startIndex, false);

      if (!this.root.querySelector('[data-step].is-active')) {
        this.goToStep(this.resolveAvailableStepIndex(0, 1), false);
      }
    }

    isWegovyPillPatient() {
      return isWegovyPillMedication(
        this.data.productPurchased,
        this.data.lastProductName,
        this.forceMedKey
      );
    }

    isFoundayoPatient() {
      return isFoundayoMedication(
        this.data.productPurchased,
        this.data.lastProductName,
        this.forceMedKey
      );
    }

    isTabletPatient() {
      return isTabletMedication(
        this.data.productPurchased,
        this.data.lastProductName,
        this.forceMedKey
      );
    }

    getActiveMedicationKey() {
      return resolveMedicationKey(
        this.data.productPurchased,
        this.data.lastProductName,
        this.forceMedKey
      );
    }

    applyForceMedicationFromUrlParams() {
      if (!this.forceMedKey) return;

      const label = getForcedProductLabel(this.forceMedKey);
      if (!label) return;

      this.data.forceMedKey = this.forceMedKey;
      this.data.productPurchased = label;
      this.data.lastProductName = label;
      if (!this.data.lastProductDose) this.data.lastProductDose = '';
      this.setLastTreatmentDisplay(label, this.data.lastProductDose);
      this.updateLastDoseHeading();
      this.saveData();
    }

    isRequestingMedicationSwitch() {
      return isMedicationSwitchChoice(this.data[MEDICATION_CHOICE_KEY]);
    }

    isSwitchingToWegovyPill() {
      if (!this.isRequestingMedicationSwitch()) return false;
      const previousName = this.data.productPurchased || this.data.lastProductName || '';
      const previousFamily = normalizeProductFamily(previousName);
      const targetFamily = resolveTargetMedicationChoice(
        this.data[MEDICATION_CHOICE_KEY],
        previousFamily
      );
      return targetFamily === 'WegovyPill';
    }

    isSwitchingToFoundayo() {
      if (!this.isRequestingMedicationSwitch()) return false;
      const previousName = this.data.productPurchased || this.data.lastProductName || '';
      const previousFamily = normalizeProductFamily(previousName);
      const targetFamily = resolveTargetMedicationChoice(
        this.data[MEDICATION_CHOICE_KEY],
        previousFamily
      );
      return targetFamily === 'Foundayo';
    }

    isSwitchingToTablet() {
      return this.isSwitchingToWegovyPill() || this.isSwitchingToFoundayo();
    }

    isEnhancedWegovyPillDoseSelected() {
      if (!this.isSwitchingToWegovyPill()) return false;
      return isEnhancedWegovyPillDoseMg(toDoseNumber(this.data[NEXT_DOSE_PREFERENCE_KEY]));
    }

    isEnhancedFoundayoDoseSelected() {
      if (!this.isSwitchingToFoundayo()) return false;
      return isEnhancedFoundayoDoseMg(toDoseNumber(this.data[NEXT_DOSE_PREFERENCE_KEY]));
    }

    isEnhancedTabletDoseSelected() {
      return this.isEnhancedWegovyPillDoseSelected() || this.isEnhancedFoundayoDoseSelected();
    }

    getTabletSwitchDoseOptions() {
      if (this.isSwitchingToFoundayo()) return this.getFoundayoSwitchDoseOptions();
      return this.getWegovyPillSwitchDoseOptions();
    }

    getFoundayoSwitchDoseOptions() {
      // Foundayo has no treatment history yet — only on-label 0.8mg.
      const doseMgs = getFoundayoSwitchDoseMgs();

      const args = this.buildRecommendDoseArgs();
      const dosesRaw = args.doses || [];
      const doses = dosesRaw
        .map((v) => {
          const value = normDose(toDoseNumber(getDoseLabel(v)));
          return {
            value,
            id: v.shopify_variant_id ?? v.id,
            label: getDoseLabel(v)
          };
        })
        .filter((d) => !Number.isNaN(d.value) && d.id);

      return doseMgs.map((mg) => {
        const match = doses.find((d) => d.value === normDose(mg));
        const label = Number.isInteger(mg) ? `${mg}mg` : `${mg}mg`;
        return {
          name: match?.label || label,
          value: match?.id || 0,
          mg
        };
      });
    }

    getWegovyPillSwitchDoseOptions() {
      const previousName = this.data.productPurchased || this.data.lastProductName || '';
      const previousFamily = normalizeProductFamily(previousName);
      const current = normDose(toDoseNumber(resolveLastProductDose(
        this.data.lastProductDose,
        this.data.productPurchased,
        this.data.lastProductName
      )));
      const mappedMg = getMappedInjectableToWegovyPillMg(current, previousFamily);

      const daysSince = getDaysSinceLastDose(this.data);
      const gapGreaterThan27 = daysSince != null && daysSince > 27;
      const doseMgs = getWegovyPillSwitchDoseMgs(mappedMg, gapGreaterThan27);

      const args = this.buildRecommendDoseArgs();
      const dosesRaw = args.doses || [];
      const doses = dosesRaw
        .map((v) => {
          const value = normDose(toDoseNumber(getDoseLabel(v)));
          return {
            value,
            id: v.shopify_variant_id ?? v.id,
            label: getDoseLabel(v)
          };
        })
        .filter((d) => !Number.isNaN(d.value) && d.id);

      return doseMgs.map((mg) => {
        const match = doses.find((d) => d.value === normDose(mg));
        const label = Number.isInteger(mg) ? `${mg}mg` : `${mg}mg`;
        return {
          name: match?.label || label,
          value: match?.id || 0,
          mg
        };
      });
    }

    hasEnhancedWegovyPillSwitchOptions() {
      if (!this.isSwitchingToWegovyPill()) return false;
      return this.getWegovyPillSwitchDoseOptions().some((option) => isEnhancedWegovyPillDoseMg(option.mg));
    }

    hasEnhancedFoundayoSwitchOptions() {
      if (!this.isSwitchingToFoundayo()) return false;
      return this.getFoundayoSwitchDoseOptions().some((option) => isEnhancedFoundayoDoseMg(option.mg));
    }

    hasEnhancedTabletSwitchOptions() {
      return this.hasEnhancedWegovyPillSwitchOptions() || this.hasEnhancedFoundayoSwitchOptions();
    }

    getEnhancedTabletDisclaimerText() {
      if (this.isEnhancedFoundayoDoseSelected()) return FOUNDAYO_DISCLAIMER_TEXT;
      if (this.isEnhancedWegovyPillDoseSelected()) return WEGOVY_PILL_DISCLAIMER_TEXT;
      return 'N/A';
    }

    clearNextDosePreferenceData() {
      delete this.data[NEXT_DOSE_PREFERENCE_KEY];
      delete this.data[NEXT_DOSE_DECREASE_REASON_KEY];
      delete this.data[NEXT_DOSE_DECREASE_NOTES_FIELD];
      delete this.data[NEXT_DOSE_PRESCRIBER_NOTES_FIELD];
      delete this.data[NEXT_DOSE_STOP_REASON_FIELD];
      delete this.data.enhancedSwitchAcknowledged;
    }

    isConsultationNeeded() {
      if (this.data[NEXT_DOSE_PREFERENCE_KEY] === NEXT_DOSE_STOP) return true;
      if (this.data.consultationNeededDueToGap) return true;
      const daysSince = getDaysSinceLastDose(this.data);
      return daysSince != null && daysSince > CONSULTATION_GAP_DAYS;
    }

    updateConsultationGapFlag() {
      const daysSince = getDaysSinceLastDose(this.data);
      this.data.consultationNeededDueToGap = daysSince != null && daysSince > CONSULTATION_GAP_DAYS;
      return this.data.consultationNeededDueToGap;
    }

    goToConsultationNeeded() {
      const index = STEPS.indexOf('consultation-needed');
      if (index < 0) return;
      this.goToStep(index);
    }

    isStepAvailable(stepName) {
      if (!stepName) return false;

      const consultationNeeded = this.isConsultationNeeded();
      if (stepName === 'consultation-needed') return consultationNeeded;
      if (consultationNeeded && STOP_TREATMENT_SKIP_STEPS.has(stepName)) return false;

      // After last-dose date, a gap > 112 skips the rest of WBQ and shows consultation needed.
      if (
        consultationNeeded
        && stepName !== 'intro'
        && stepName !== 'last-injection-date'
        && stepName !== 'consultation-needed'
      ) {
        return false;
      }

      // Switch requests skip next-dose preference except tablet products (mg selection).
      if (
        stepName === 'next-dose-preference'
        && this.isRequestingMedicationSwitch()
        && !this.isSwitchingToTablet()
      ) {
        return false;
      }

      // Pill/tablet has no sharps bin or needles — skip accessories entirely.
      if (stepName === 'delivery-accessories' && !this.needsDeliveryAccessoriesStep()) return false;

      if (this.isTabletPatient()) {
        if (TABLET_SKIP_STEPS.has(stepName)) return false;
        return true;
      }
      if (INJECTABLE_SKIP_STEPS.has(stepName)) return false;
      return true;
    }

    getVisibleStepIndexes() {
      const indexes = [];
      for (let i = 0; i < STEPS.length; i += 1) {
        if (this.isStepAvailable(STEPS[i])) indexes.push(i);
      }
      return indexes;
    }

    resolveAvailableStepIndex(index, direction) {
      const dir = direction === -1 ? -1 : 1;
      let i = index;
      while (i >= 0 && i < STEPS.length) {
        if (this.isStepAvailable(STEPS[i])) return i;
        i += dir;
      }
      if (dir === 1) {
        for (let j = index - 1; j >= 0; j -= 1) {
          if (this.isStepAvailable(STEPS[j])) return j;
        }
      } else {
        for (let j = index + 1; j < STEPS.length; j += 1) {
          if (this.isStepAvailable(STEPS[j])) return j;
        }
      }
      return 0;
    }

    ensureCurrentStepAvailable() {
      this.updateLastDoseHeading();
      if (this.isStepAvailable(STEPS[this.currentStep])) return;
      const next = this.resolveAvailableStepIndex(this.currentStep, 1);
      if (next !== this.currentStep) this.goToStep(next, false);
    }

    updateLastDoseHeading() {
      const heading = this.root.querySelector('[data-last-dose-heading]');
      if (!heading) return;
      heading.textContent = this.isTabletPatient()
        ? LAST_DOSE_HEADING_PILL
        : LAST_DOSE_HEADING_INJECTION;
    }

    bindEvents() {
      this.root.addEventListener('click', (e) => this.handleClick(e));
      this.root.addEventListener('input', (e) => this.handleInput(e));
      this.root.addEventListener('change', (e) => this.handleChange(e));
      this.root.addEventListener('blur', (e) => this.handleBlur(e), true);
    }

    handleClick(e) {
      const toggle = e.target.closest('[data-unit-toggle]');
      if (toggle) {
        this.handleUnitToggle(toggle);
        return;
      }

      const travelUploadTrigger = e.target.closest('[data-travel-upload-trigger]');
      if (travelUploadTrigger) {
        this.initTravelProofUpload();
        if (this.travelUploadcareWidget) this.travelUploadcareWidget.openDialog();
        return;
      }

      const optionalExtraSkip = e.target.closest('[data-optional-extras-skip]');
      if (optionalExtraSkip) {
        this.goToDeliveryDateStep();
        return;
      }

      const optionalExtraAdd = e.target.closest('[data-optional-extra-add]');
      if (optionalExtraAdd && !optionalExtraAdd.disabled) {
        this.handleOptionalExtraAdd(optionalExtraAdd);
        return;
      }

      const deliveryPrev = e.target.closest('[data-delivery-prev]');
      if (deliveryPrev) {
        this.shiftDeliveryCalendarMonth(-1);
        return;
      }

      const deliveryNext = e.target.closest('[data-delivery-next]');
      if (deliveryNext) {
        this.shiftDeliveryCalendarMonth(1);
        return;
      }

      const deliveryDay = e.target.closest('[data-delivery-day]');
      if (deliveryDay && !deliveryDay.disabled) {
        this.selectDeliveryDate(deliveryDay.dataset.date);
        return;
      }

      const deliverySubmit = e.target.closest('[data-delivery-submit]');
      if (deliverySubmit && !deliverySubmit.disabled) {
        void this.submitDeliveryDate();
        return;
      }

      const selector = e.target.closest('[data-selector]');
      if (selector) {
        this.handleSelector(selector);
        return;
      }

      const backBtn = e.target.closest('[data-wbq-back]');
      if (backBtn) {
        this.goBack();
        return;
      }

      const continueBtn = e.target.closest('[data-wbq-continue]');
      if (continueBtn) {
        if (continueBtn.classList.contains('is-disabled')) {
          this.validateCurrentStep();
          return;
        }
        void this.goForward();
        return;
      }

      const assessmentBtn = e.target.closest('[data-wbq-start-assessment]');
      if (assessmentBtn) {
        this.goToAssessment();
      }
    }

    handleChange(e) {
      if (e.target.matches('[data-optional-extra-select]')) {
        this.updateOptionalExtraSelectState(e.target);
        return;
      }

      if (e.target.matches('[data-travel-schedule-checkbox]')) {
        this.data[TRAVEL_SCHEDULE_REVIEW_KEY] = e.target.checked;
        this.clearGroupError('travelScheduleReview');
        this.saveData();
        this.updateContinueState();
        return;
      }

      if (e.target.matches('[data-enhanced-switch-checkbox]')) {
        this.data.enhancedSwitchAcknowledged = e.target.checked;
        this.clearGroupError('enhancedSwitchAck');
        this.saveData();
        this.updateContinueState();
      }
    }

    handleInput(e) {
      const field = e.target.closest('[name]');
      if (!field) return;

      if (LAST_DOSE_FIELDS.includes(field.name)) {
        this.data[field.name] = field.value;
        this.clearLastDoseErrors();
        if (this.isLastInjectionDateComplete()) {
          this.validateLastInjectionDate();
        } else {
          this.data.consultationNeededDueToGap = false;
        }
        this.updateRecommendedDose();
        this.saveData();
        this.updateContinueState();
        return;
      }

      if (TRAVEL_DATE_FIELDS.includes(field.name)) {
        this.data[field.name] = field.value;
        this.clearFieldError(field.name);
        this.clearGroupError('travelDates');
        this.saveData();
        this.updateContinueState();
        return;
      }

      if (WBQ_DETAIL_TEXTAREA_FIELDS.has(field.name)) {
        this.data[field.name] = field.value;
        this.saveData();
        field.classList.remove('is-error');
        this.updateContinueState();
        return;
      }

      if (CURRENT_WEIGHT_INTEGER_FIELDS.has(field.name)) {
        const before = field.value;
        const cleaned = sanitizeIntegerInput(before);
        if (before !== cleaned) {
          field.value = cleaned;
          this.showFieldError(field.name, BODY_MEASUREMENT_RANGES.NUMBERS_ONLY);
        }
        this.data[field.name] = field.value;
        this.saveData();
        this.validateCurrentWeightField(field.name);
        this.updateContinueState();
        return;
      }

      if (CURRENT_WEIGHT_DECIMAL_FIELDS.has(field.name)) {
        const before = field.value;
        const cleaned = sanitizeDecimalInput(before);
        if (before !== cleaned) {
          field.value = cleaned;
          this.showFieldError(field.name, BODY_MEASUREMENT_RANGES.NUMBERS_ONLY);
        }
        this.data[field.name] = field.value;
        this.saveData();
        this.validateCurrentWeightField(field.name);
        this.updateContinueState();
      }
    }

    handleBlur(e) {
      const field = e.target.closest('[name]');
      if (!field) return;

      if (LAST_DOSE_FIELDS.includes(field.name)) {
        if (STEPS[this.currentStep] !== 'last-injection-date') return;
        this.validateLastInjectionDate({ showFieldErrors: true });
        this.updateContinueState();
        return;
      }

      if (TRAVEL_DATE_FIELDS.includes(field.name)) {
        if (STEPS[this.currentStep] !== 'medication-supply') return;
        this.validateTravelDates({ showFieldErrors: true, requireThreeWeeks: false });
        this.updateContinueState();
        return;
      }

      if (CURRENT_WEIGHT_INTEGER_FIELDS.has(field.name) || CURRENT_WEIGHT_DECIMAL_FIELDS.has(field.name)) {
        if (STEPS[this.currentStep] !== 'current-weight') return;
        this.validateCurrentWeightField(field.name);
        this.updateContinueState();
      }
    }

    getFieldValue(name) {
      const field = this.root.querySelector(`[name="${name}"]`);
      return field ? field.value.trim() : '';
    }

    setFieldValue(name, value) {
      const field = this.root.querySelector(`[name="${name}"]`);
      if (field) field.value = value != null ? String(value) : '';
    }

    restoreLastDoseFields() {
      LAST_DOSE_FIELDS.forEach((name) => {
        if (this.data[name] != null) {
          this.setFieldValue(name, this.data[name]);
        }
      });
    }

    applyCurrentMonthYearPrefill() {
      const now = new Date();
      let dirty = false;

      if (!String(this.data.lastDoseMonth || '').trim() && !this.getFieldValue('lastDoseMonth')) {
        this.data.lastDoseMonth = String(now.getMonth() + 1);
        dirty = true;
      }
      if (!String(this.data.lastDoseYear || '').trim() && !this.getFieldValue('lastDoseYear')) {
        this.data.lastDoseYear = String(now.getFullYear());
        dirty = true;
      }

      if (dirty) this.saveData();
      this.restoreLastDoseFields();
    }

    clearFieldError(name) {
      const field = this.root.querySelector(`[name="${name}"]`);
      const wrap = field?.closest('[data-field]');
      const errorEl = wrap?.querySelector('[data-field-error]');
      if (field) field.classList.remove('is-error');
      if (errorEl) {
        errorEl.textContent = '';
        errorEl.classList.remove('is-visible');
      }
    }

    showFieldError(name, message) {
      const field = this.root.querySelector(`[name="${name}"]`);
      const wrap = field?.closest('[data-field]');
      const errorEl = wrap?.querySelector('[data-field-error]');
      if (field) field.classList.add('is-error');
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.classList.add('is-visible');
      }
    }

    clearLastDoseErrors() {
      LAST_DOSE_FIELDS.forEach((name) => this.clearFieldError(name));
      const banner = this.root.querySelector('[data-last-dose-error]');
      if (banner) banner.classList.remove('is-visible');
    }

    showLastDoseBanner(message) {
      const banner = this.root.querySelector('[data-last-dose-error]');
      const textEl = this.root.querySelector('[data-last-dose-error-text]');
      if (textEl) textEl.textContent = message || 'Invalid date';
      if (banner) banner.classList.add('is-visible');
    }

    validateLastInjectionDate(options = {}) {
      const { showFieldErrors = false } = options;
      this.clearLastDoseErrors();

      const dayValue = this.getFieldValue('lastDoseDay');
      const monthValue = this.getFieldValue('lastDoseMonth');
      const yearValue = this.getFieldValue('lastDoseYear');

      if (showFieldErrors) {
        if (!dayValue) this.showFieldError('lastDoseDay', 'Please enter a day.');
        if (!monthValue) this.showFieldError('lastDoseMonth', 'Please enter a month.');
        if (!yearValue) this.showFieldError('lastDoseYear', 'Please enter a year.');
      }

      const result = parseLastInjectionDate(dayValue, monthValue, yearValue);

      if (!result.valid) {
        if (showFieldErrors && result.field && result.message && (dayValue && monthValue && yearValue)) {
          if (result.banner) {
            this.showLastDoseBanner(result.message);
          } else {
            this.showFieldError(result.field, result.message);
          }
        }
        return false;
      }

      this.data.lastDoseDay = String(result.day);
      this.data.lastDoseMonth = String(result.month);
      this.data.lastDoseYear = String(result.year);
      this.data.lastDoseDateIso = result.iso;
      this.updateConsultationGapFlag();
      this.updateRecommendedDose();
      this.saveData();
      return true;
    }

    isLastInjectionDateComplete() {
      return parseLastInjectionDate(
        this.getFieldValue('lastDoseDay'),
        this.getFieldValue('lastDoseMonth'),
        this.getFieldValue('lastDoseYear')
      ).valid;
    }

    validateCurrentStep() {
      const step = STEPS[this.currentStep];
      if (step === 'last-injection-date') {
        return this.validateLastInjectionDate({ showFieldErrors: true });
      }
      if (step === 'injection-twice') {
        return this.data[INJECTION_TWICE_KEY] === 'yes';
      }
      if (step === 'injection-pen-issues') {
        return this.validateInjectionPenIssues({ showFieldErrors: true });
      }
      if (step === 'current-weight') {
        return this.validateCurrentWeight({ showFieldErrors: true });
      }
      if (step === 'side-effects') {
        return this.validateSideEffects({ showFieldErrors: true });
      }
      if (step === 'medication-regularity') {
        return !!this.data[MEDICATION_REGULARITY_KEY];
      }
      if (step === 'pill-adherence') {
        return this.validatePillMissedDuration({ showFieldErrors: true });
      }
      if (step === 'pill-tablet-adherence') {
        return this.validatePillTabletAdherence({ showFieldErrors: true });
      }
      if (step === 'weight-loss-methods') {
        return this.validateWeightLossMethods({ showFieldErrors: true });
      }
      if (step === 'health-changes') {
        return this.validateHealthChanges({ showFieldErrors: true });
      }
      if (step === 'medication-choice') {
        if (!this.data[MEDICATION_CHOICE_KEY]) {
          this.showGroupError(MEDICATION_CHOICE_KEY, 'Please choose an option.');
          return false;
        }
        return true;
      }
      if (step === 'next-dose-preference') {
        return this.validateNextDosePreference({ showFieldErrors: true });
      }
      if (step === 'medication-supply') {
        return this.validateMedicationSupply({ showFieldErrors: true });
      }
      if (step === 'delivery-accessories') {
        return this.validateDeliveryAccessories({ showFieldErrors: true });
      }
      if (step === 'optional-extras') {
        return true;
      }
      if (step === 'delivery-date') {
        return this.validateDeliveryDate();
      }
      return true;
    }

    isOptionalExtraSelected(variantId) {
      const id = Number(variantId);
      return (this.data.optionalExtras || []).some((extraId) => Number(extraId) === id);
    }

    updateOptionalExtraSelectState(select) {
      const card = select.closest('[data-optional-extra-card]');
      const btn = card?.querySelector('[data-optional-extra-add]');
      if (!btn) return;

      const groupVariantIds = (card.dataset.optionalExtraVariants || '')
        .split(',')
        .map((id) => Number(id))
        .filter((id) => id > 0);

      const selectedInGroup = groupVariantIds.find((id) => this.isOptionalExtraSelected(id));
      if (selectedInGroup && !select.value) {
        select.value = String(selectedInGroup);
      }

      const variantId = Number(select.value);
      if (variantId > 0) {
        btn.dataset.variantId = String(variantId);
        btn.disabled = false;
        btn.classList.toggle('is-added', this.isOptionalExtraSelected(variantId));
        btn.textContent = this.isOptionalExtraSelected(variantId) ? 'Added' : 'Add to cart';
      } else {
        delete btn.dataset.variantId;
        btn.disabled = true;
        btn.classList.remove('is-added');
        btn.textContent = 'Add to cart';
      }
    }

    updateOptionalExtrasUI() {
      this.root.querySelectorAll('[data-optional-extra-add]').forEach((btn) => {
        const variantId = Number(btn.dataset.variantId);
        if (!variantId) return;
        const added = this.isOptionalExtraSelected(variantId);
        btn.classList.toggle('is-added', added);
        btn.textContent = added ? 'Added' : 'Add to cart';
      });

      this.root.querySelectorAll('[data-optional-extra-select]').forEach((select) => {
        this.updateOptionalExtraSelectState(select);
      });
    }

    handleOptionalExtraAdd(btn) {
      const variantId = Number(btn.dataset.variantId);
      if (!variantId) return;

      if (!Array.isArray(this.data.optionalExtras)) this.data.optionalExtras = [];

      const card = btn.closest('[data-optional-extra-card]');
      const groupVariantIds = (card?.dataset.optionalExtraVariants || '')
        .split(',')
        .map((id) => Number(id))
        .filter((id) => id > 0);

      const index = this.data.optionalExtras.findIndex((id) => Number(id) === variantId);
      if (index >= 0) {
        this.data.optionalExtras.splice(index, 1);
        btn.classList.remove('is-added');
        btn.textContent = 'Add to cart';
      } else {
        if (groupVariantIds.length) {
          this.data.optionalExtras = this.data.optionalExtras.filter(
            (id) => !groupVariantIds.includes(Number(id))
          );
        }
        this.data.optionalExtras.push(variantId);
        btn.classList.add('is-added');
        btn.textContent = 'Added';
      }

      this.saveData();
      this.updateOptionalExtrasUI();

      if (index < 0) {
        this.goToDeliveryDateStep();
      }
    }

    goToDeliveryDateStep() {
      const deliveryIndex = STEPS.indexOf('delivery-date');
      if (deliveryIndex >= 0) {
        this.goToStep(deliveryIndex);
      }
    }

    getDeliveryCalendarViewDate() {
      if (this.deliveryCalendarView) {
        return new Date(this.deliveryCalendarView.year, this.deliveryCalendarView.month, 1);
      }

      const minDate = this.getMinDeliveryDate();
      if (this.data.deliveryDate) {
        const selected = new Date(this.data.deliveryDate + 'T00:00:00');
        if (!Number.isNaN(selected.getTime())) {
          this.deliveryCalendarView = { year: selected.getFullYear(), month: selected.getMonth() };
          return new Date(this.deliveryCalendarView.year, this.deliveryCalendarView.month, 1);
        }
      }

      this.deliveryCalendarView = { year: minDate.getFullYear(), month: minDate.getMonth() };
      return new Date(this.deliveryCalendarView.year, this.deliveryCalendarView.month, 1);
    }

    getMinDeliveryDate() {
      const min = this.getBrowserTodayDate();
      min.setDate(min.getDate() + this.deliveryMinDays);
      return min;
    }

    getBrowserTodayDate() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    }

    getMaxDeliveryDate() {
      const max = this.getBrowserTodayDate();
      max.setDate(max.getDate() + this.deliveryMaxDays);
      return max;
    }

    isDeliveryDateSelectable(isoDate) {
      const date = new Date(isoDate + 'T00:00:00');
      if (Number.isNaN(date.getTime())) return false;
      if (isPermanentDeliveryDate(isoDate, this.deliveryBlockedDates, this.deliveryBlockedRanges)) return false;
      if (DELIVERY_DISABLED_WEEKDAYS.indexOf(date.getDay()) !== -1) return false;

      const today = this.getBrowserTodayDate();
      if (date < today) return false;

      const min = this.getMinDeliveryDate();
      if (date < min) return false;

      const max = this.getMaxDeliveryDate();
      if (date > max) return false;

      return true;
    }

    formatDeliveryDateLabel(isoDate) {
      const date = new Date(isoDate + 'T00:00:00');
      if (Number.isNaN(date.getTime())) return isoDate;
      return date.toLocaleDateString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }

    toIsoDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    shiftDeliveryCalendarMonth(delta) {
      const view = this.getDeliveryCalendarViewDate();
      view.setMonth(view.getMonth() + delta);
      this.deliveryCalendarView = { year: view.getFullYear(), month: view.getMonth() };
      this.renderDeliveryCalendar();
    }

    selectDeliveryDate(isoDate) {
      if (!this.isDeliveryDateSelectable(isoDate)) return;

      this.data.deliveryDate = isoDate;
      this.clearGroupError('deliveryDate');
      this.saveData();
      this.renderDeliveryCalendar();
      this.updateDeliverySubmitState();
    }

    renderDeliveryCalendar() {
      const grid = this.root.querySelector('[data-delivery-calendar-grid]');
      const label = this.root.querySelector('[data-delivery-month-label]');
      const prevBtn = this.root.querySelector('[data-delivery-prev]');
      const nextBtn = this.root.querySelector('[data-delivery-next]');
      if (!grid || !label) return;

      if (this.data.deliveryDate && !this.isDeliveryDateSelectable(this.data.deliveryDate)) {
        this.data.deliveryDate = null;
        this.saveData();
      }

      const view = this.getDeliveryCalendarViewDate();
      const year = view.getFullYear();
      const month = view.getMonth();
      const monthFormatter = new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' });
      label.textContent = monthFormatter.format(view);

      const minView = new Date(this.getMinDeliveryDate().getFullYear(), this.getMinDeliveryDate().getMonth(), 1);
      const maxView = new Date(this.getMaxDeliveryDate().getFullYear(), this.getMaxDeliveryDate().getMonth(), 1);
      const viewIndex = year * 12 + month;
      const minIndex = minView.getFullYear() * 12 + minView.getMonth();
      const maxIndex = maxView.getFullYear() * 12 + maxView.getMonth();
      if (prevBtn) prevBtn.disabled = viewIndex <= minIndex;
      if (nextBtn) nextBtn.disabled = viewIndex >= maxIndex;

      const firstDay = new Date(year, month, 1);
      const startOffset = (firstDay.getDay() + 6) % 7;
      const gridStart = new Date(year, month, 1 - startOffset);

      const cells = [];
      for (let i = 0; i < 42; i += 1) {
        const date = new Date(gridStart);
        date.setDate(gridStart.getDate() + i);
        const iso = this.toIsoDate(date);
        const inMonth = date.getMonth() === month;
        const dayOfWeek = date.getDay();
        const selectable = inMonth && this.isDeliveryDateSelectable(iso);
        const isSelected = this.data.deliveryDate === iso;
        const classes = ['wbq-form__calendar-day'];

        if (!inMonth) classes.push('is-outside');
        if (dayOfWeek === 0) classes.push('is-sunday');
        if (dayOfWeek === 1 && inMonth) classes.push('is-monday');
        if (dayOfWeek === 6 && inMonth) classes.push('is-saturday');
        if (isSelected) classes.push('is-selected');
        if (!selectable) classes.push('is-disabled');

        cells.push(
          `<button type="button" class="${classes.join(' ')}" data-delivery-day data-date="${iso}" ${selectable ? '' : 'disabled'}>${date.getDate()}</button>`
        );
      }

      grid.innerHTML = cells.join('');
      this.updateDeliverySubmitState();
    }

    updateDeliverySubmitState() {
      const btn = this.root.querySelector('[data-delivery-submit]');
      if (!btn) return;
      const enabled = !!this.data.deliveryDate;
      btn.disabled = !enabled;
      btn.classList.toggle('is-disabled', !enabled);
    }

    validateDeliveryDate() {
      if (!this.data.deliveryDate || !this.isDeliveryDateSelectable(this.data.deliveryDate)) {
        return this.showGroupError('deliveryDate', 'Please select a delivery date.');
      }
      this.saveData();
      return true;
    }

    clearWbqSubmitError() {
      const errorEl = this.root.querySelector('[data-group-error="wbqSubmit"]');
      if (!errorEl) return;
      errorEl.textContent = '';
      errorEl.classList.remove('is-visible', 'is-success');
    }

    showWbqSubmitError(message) {
      const errorEl = this.root.querySelector('[data-group-error="wbqSubmit"]');
      if (!errorEl) return false;
      errorEl.classList.remove('is-success');
      errorEl.textContent = message;
      errorEl.classList.add('is-visible');
      return false;
    }

    showWbqSubmitProgress(message) {
      const submitBtn = this.root.querySelector('[data-delivery-submit]');
      const statusEl = this.root.querySelector('[data-wbq-submit-status]');

      if (statusEl) {
        statusEl.textContent = message;
        statusEl.hidden = false;
        statusEl.classList.add('is-visible');
      }

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = message;
      }
    }

    hideWbqSubmitProgress() {
      const statusEl = this.root.querySelector('[data-wbq-submit-status]');
      if (!statusEl) return;
      statusEl.textContent = '';
      statusEl.hidden = true;
      statusEl.classList.remove('is-visible');
    }

    showWbqSubmitSuccess(message) {
      const resolvedMessage = message || 'Check-in saved successfully.';
      const errorEl = this.root.querySelector('[data-group-error="wbqSubmit"]');
      const resultEl = this.root.querySelector('[data-wbq-submit-result]');
      const titleEl = this.root.querySelector('[data-wbq-submit-title]');
      const detailEl = this.root.querySelector('[data-wbq-submit-detail]');
      const calendarEl = this.root.querySelector('[data-delivery-calendar]');
      const submitBtn = this.root.querySelector('[data-delivery-submit]');
      const backBtn = this.root.querySelector('[data-step="delivery-date"] [data-wbq-back]');
      const actionsEl = this.root.querySelector('[data-step="delivery-date"] .wbq-form__actions');

      this.hideWbqSubmitProgress();

      if (errorEl) {
        errorEl.textContent = resolvedMessage;
        errorEl.classList.add('is-visible', 'is-success');
      }

      if (titleEl) titleEl.textContent = resolvedMessage;

      if (detailEl) {
        const preview = this.data.deliveryDate
          ? `Preferred delivery date: ${this.formatDeliveryDateLabel(this.data.deliveryDate)}`
          : '';
        if (preview) {
          detailEl.textContent = preview;
          detailEl.hidden = false;
        } else {
          detailEl.textContent = '';
          detailEl.hidden = true;
        }
      }

      if (resultEl) {
        resultEl.hidden = false;
        resultEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      if (calendarEl) {
        const header = calendarEl.querySelector('.wbq-form__calendar-header');
        const weekdays = calendarEl.querySelector('.wbq-form__calendar-weekdays');
        const grid = calendarEl.querySelector('[data-delivery-calendar-grid]');
        if (header) header.hidden = true;
        if (weekdays) weekdays.hidden = true;
        if (grid) grid.hidden = true;
      }

      if (actionsEl) actionsEl.hidden = true;

      if (submitBtn) {
        submitBtn.hidden = true;
        submitBtn.disabled = true;
      }

      if (backBtn) backBtn.hidden = true;
    }

    getCheckoutFirstName() {
      const patient = this.data.quizPlpPatient || {};
      return String(
        patient.patientFirstName
        || patient.firstName
        || patient.first_name
        || this.data.firstName
        || ''
      ).trim();
    }

    getCheckoutLastName() {
      const patient = this.data.quizPlpPatient || {};
      return String(
        patient.lastName
        || patient.last_name
        || this.data.lastName
        || ''
      ).trim();
    }

    buildWbqCheckoutQS() {
      const params = new URLSearchParams();
      const email = this.getPatientEmail();
      const firstName = this.getCheckoutFirstName();
      const lastName = this.getCheckoutLastName();

      if (email) {
        params.set('checkout[email]', email);
      }
      if (firstName) {
        params.set('checkout[shipping_address][first_name]', firstName);
      }
      if (lastName) {
        params.set('checkout[shipping_address][last_name]', lastName);
      }
      if (this.data.deliveryDate) {
        params.set('attributes[Delivery date]', this.data.deliveryDate);
        params.set('attributes[Shipping Type]', 'weekday');
        params.set('note', `Delivery date:${this.data.deliveryDate}`);
      }

      return params.toString();
    }

    buildCheckoutUrl() {
      const checkoutQS = this.buildWbqCheckoutQS();
      const step = 'step=contact_information';
      return checkoutQS
        ? `/checkout?${checkoutQS}&${step}`
        : `/checkout?${step}`;
    }

    cartPermalinkCheckoutItems(items) {
      const lines = (items || [])
        .map((item) => ({ id: Number(item.id), qty: Number(item.qty) || 1 }))
        .filter((item) => item.id > 0);

      if (!lines.length) return null;

      const checkoutQS = this.buildWbqCheckoutQS();
      // Match Assessment permalink format (no trailing comma — trailing comma can send Shopify to /).
      const base = `/cart/${lines.map((item) => `${encodeURIComponent(String(item.id))}:${encodeURIComponent(String(item.qty))}`).join(',')}`;
      const step = 'step=contact_information';
      if (!checkoutQS) return `${base}?${step}`;
      return `${base}?${checkoutQS}&${step}`;
    }

    clearCart() {
      return fetch('/cart/clear', { method: 'POST', credentials: 'same-origin' });
    }

    buildRecommendDoseArgs() {
      const previousName = this.data.productPurchased || this.data.lastProductName || '';
      const previousFamily = normalizeProductFamily(previousName);
      const targetFamily = resolveTargetMedicationChoice(
        this.data[MEDICATION_CHOICE_KEY],
        previousFamily
      );
      const direction = resolveDoseDirection(
        this.data[NEXT_DOSE_PREFERENCE_KEY],
        this.data[MEDICATION_CHOICE_KEY],
        previousFamily,
        targetFamily
      );

      const familyForLookup = targetFamily || previousFamily;
      const productLabel =
        familyForLookup === 'Foundayo' ? MEDICATION_NAME_FOUNDAYO :
        familyForLookup === 'WegovyPill' ? MEDICATION_NAME_WEGOVY_PILL :
        familyForLookup === 'Mounjaro' ? MEDICATION_NAME_MOUNJARO :
        familyForLookup === 'Wegovy' ? MEDICATION_NAME_WEGOVY :
        previousName;

      const apiProduct = findApiProduct(this.data.quizPlpProducts, familyForLookup);
      const doses = Array.isArray(apiProduct?.variants)
        ? apiProduct.variants
        : (Array.isArray(apiProduct?.doses) ? apiProduct.doses : []);

      return {
        doses,
        currentDose: resolveLastProductDose(
          this.data.lastProductDose,
          this.data.productPurchased,
          this.data.lastProductName
        ),
        productName: productLabel,
        previousProduct: previousName,
        direction,
        selectedDoseMg: this.isSwitchingToTablet()
          ? toDoseNumber(this.data[NEXT_DOSE_PREFERENCE_KEY])
          : undefined,
        dd: this.data.lastDoseDay,
        mm: this.data.lastDoseMonth,
        yy: this.data.lastDoseYear
      };
    }

    updateRecommendedDose() {
      const result = recommendDose(this.buildRecommendDoseArgs());
      if (!result) {
        delete this.data.recommendedDose;
        delete this.data.recommendedDoseVariantId;
        return null;
      }

      this.data.recommendedDose = result;
      const variantId = Number(result.id);
      if (variantId) {
        this.data.recommendedDoseVariantId = variantId;
      } else {
        delete this.data.recommendedDoseVariantId;
      }
      return result;
    }

    buildCreateWellbeingPayload() {
      this.updateRecommendedDose();

      const previousName = this.data.productPurchased || this.data.lastProductName || '';
      const previousFamily = normalizeProductFamily(previousName);
      const targetFamily = resolveTargetMedicationChoice(
        this.data[MEDICATION_CHOICE_KEY],
        previousFamily
      );
      const direction = resolveDoseDirection(
        this.data[NEXT_DOSE_PREFERENCE_KEY],
        this.data[MEDICATION_CHOICE_KEY],
        previousFamily,
        targetFamily
      );

      const sideEffects = Array.isArray(this.data[SIDE_EFFECTS_KEY])
        ? [...this.data[SIDE_EFFECTS_KEY]]
        : [];
      if (
        sideEffects.includes(SIDE_EFFECTS_OTHER_VALUE)
        && this.data[SIDE_EFFECTS_OTHER_FIELD]
      ) {
        const idx = sideEffects.indexOf(SIDE_EFFECTS_OTHER_VALUE);
        if (idx >= 0) sideEffects[idx] = this.data[SIDE_EFFECTS_OTHER_FIELD];
      }

      const weightMethods = Array.isArray(this.data[WEIGHT_LOSS_METHODS_KEY])
        ? [...this.data[WEIGHT_LOSS_METHODS_KEY]]
        : [];
      if (
        weightMethods.includes(WEIGHT_LOSS_METHODS_OTHER_VALUE)
        && this.data[WEIGHT_LOSS_METHODS_OTHER_FIELD]
      ) {
        const idx = weightMethods.indexOf(WEIGHT_LOSS_METHODS_OTHER_VALUE);
        if (idx >= 0) weightMethods[idx] = this.data[WEIGHT_LOSS_METHODS_OTHER_FIELD];
      }

      const weightKg = Number(this.data.currentWeightKg || this.parseWeightKg('current'));
      const isTravel = this.data[MEDICATION_SUPPLY_KEY] === MEDICATION_SUPPLY_TRAVEL;
      const phone = this.data.quizPlpPatient?.phoneNumber
        || this.data.phone
        || this.data.phone_number
        || '';

      let doseReductionReason = this.data[NEXT_DOSE_DECREASE_REASON_KEY] || '';
      if (this.data[NEXT_DOSE_DECREASE_NOTES_FIELD]) {
        doseReductionReason = doseReductionReason
          ? `${doseReductionReason}. ${this.data[NEXT_DOSE_DECREASE_NOTES_FIELD]}`
          : this.data[NEXT_DOSE_DECREASE_NOTES_FIELD];
      }

      const isFoundayo = this.isFoundayoPatient();
      const pillMissedDuration = this.data[PILL_MISSED_DURATION_KEY] || '';
      const pillStoppedReason = this.data[PILL_STOPPED_REASON_FIELD] || '';
      const pillTabletAdherence = this.data[PILL_TABLET_ADHERENCE_KEY] || '';

      const payload = {
        last_administration_date: formatApiDateParts(
          this.data.lastDoseDay,
          this.data.lastDoseMonth,
          this.data.lastDoseYear
        ) || this.data.lastDoseDateIso || '',
        treatment_gap_details: isFoundayo ? '' : pillStoppedReason,
        injected_current_dose_twice: yesNoLabel(this.data[INJECTION_TWICE_KEY]),
        injection_pen_issues: yesNoLabel(this.data[INJECTION_PEN_ISSUES_KEY]),
        injection_pen_issues_details: this.data[INJECTION_PEN_ISSUES_DETAIL_FIELD] || '',
        weight_in_kg: Number.isFinite(weightKg) && weightKg > 0 ? weightKg : '',
        medication_schedule_gap: isFoundayo ? '' : pillMissedDuration,
        wegovy_instruction_adherence: isFoundayo ? '' : pillTabletAdherence,
        foundayo_dose_break: isFoundayo ? pillMissedDuration : '',
        foundayo_dose_break_details: isFoundayo ? pillStoppedReason : '',
        foundayo_instruction: isFoundayo ? pillTabletAdherence : '',
        side_effects_list: formatJoinedList(sideEffects),
        side_effects_impact: formatJoinedList(this.data[SIDE_EFFECTS_IMPACT_KEY] || []),
        side_effects_management: this.data[SIDE_EFFECTS_MANAGEMENT_FIELD] || '',
        medication_adherence: this.data[MEDICATION_REGULARITY_KEY] || '',
        additional_weight_loss_methods: formatJoinedList(weightMethods),
        health_changes_since_last_review: yesNoLabel(this.data[HEALTH_CHANGES_KEY]),
        health_changes_details: this.data[HEALTH_CHANGES_DETAIL_FIELD] || '',
        escalate_dose: formatEscalateDose(this.data.recommendedDose, direction),
        dose_reduction_reason: doseReductionReason,
        prescriber_message: this.data[NEXT_DOSE_PRESCRIBER_NOTES_FIELD] || '',
        stop_treatment_other_input_text: this.data[NEXT_DOSE_STOP_REASON_FIELD] || '',
        request_multi_month_supply: this.data[MEDICATION_SUPPLY_KEY] || '',
        requested_supply_months: '',
        multi_month_request_reason: '',
        travel_from_date: isTravel
          ? (this.data.travelFromDateIso || formatApiDateParts(
            this.data.travelFromDay,
            this.data.travelFromMonth,
            this.data.travelFromYear
          ))
          : '',
        travel_to_date: isTravel
          ? (this.data.travelToDateIso || formatApiDateParts(
            this.data.travelToDay,
            this.data.travelToMonth,
            this.data.travelToYear
          ))
          : '',
        travel_documents: isTravel ? (this.data[TRAVEL_PROOF_UPLOAD_KEY] || null) : null,
        patient_email: this.getPatientEmail(),
        phone: String(phone || '').trim(),
        extra_needles: this.showsWegovyInjectionAccessories()
          ? yesNoFromSelector(this.data[EXTRA_NEEDLES_KEY])
          : '',
        pencycle_box: this.showsWegovyInjectionAccessories()
          ? (yesNoFromSelector(this.data[PENCYCLE_BOX_KEY]) || 'No')
          : 'No',
        sharps_bin: this.showsSharpsBinAccessory()
          ? yesNoFromSelector(this.data[SHARPS_BIN_KEY])
          : '',
        delivery_date: this.data.deliveryDate || '',
        spa_12_free_text_box: this.data.spa12FreeText || '',
        wbq_switcher: this.data[MEDICATION_CHOICE_KEY] || '',
        name_dose: formatNameDose(this.data.recommendedDose),
        enhanced_switch_requested: this.isEnhancedTabletDoseSelected() ? 'true' : 'false',
        enhanced_switch_acknowledged: this.data.enhancedSwitchAcknowledged ? 'true' : 'false',
        wegovy_pill_disclaimer: this.data.enhancedSwitchAcknowledged
          ? this.getEnhancedTabletDisclaimerText()
          : 'N/A',
        category_id: Number(this.categoryId) || 1
      };

      return payload;
    }

    isCreateWellbeingResponseSuccessful(response, result) {
      if (!response.ok && response.status !== 201) return false;
      if (result?.status === 'error' || result?.success === false) return false;
      if (result?.status === 'success') return true;
      if (result?.success === true) return true;
      return response.ok || response.status === 201;
    }

    async syncCreateWellbeingToApi() {
      const email = this.getPatientEmail();
      if (!email) return false;

      const payload = this.buildCreateWellbeingPayload();
      if (!payload.patient_email) return false;

      this.lastCreateWellbeingSubmission = payload;

      const headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Tenant': this.apiTenant
      };

      const accessToken = getAccessTokenFromCookie();
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }

      try {
        const response = await fetch(this.createWellbeingApiUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
          credentials: 'omit'
        });

        const rawText = await response.text();
        let result = {};
        if (rawText.trim()) {
          try {
            result = JSON.parse(rawText);
          } catch (_) {
            this.lastCreateWellbeingResponse = {
              ok: false,
              status: response.status,
              result: {},
              text: rawText
            };
            return false;
          }
        }

        this.lastCreateWellbeingResponse = {
          ok: response.ok || response.status === 201,
          status: response.status,
          result,
          text: rawText
        };

        if (!this.isCreateWellbeingResponseSuccessful(response, result)) {
          return false;
        }

        this.data.wellbeingSubmitted = true;
        this.saveData();
        return true;
      } catch (_) {
        return false;
      }
    }

    getCheckoutLineItems() {
      this.updateRecommendedDose();

      const seen = new Set();
      const lines = [];

      const add = (id, qty) => {
        const variantId = Number(id);
        if (!variantId || seen.has(variantId)) return;
        seen.add(variantId);
        lines.push({ id: variantId, qty: qty || 1 });
      };

      const medQty = this.data[MEDICATION_SUPPLY_KEY] === MEDICATION_SUPPLY_TRAVEL ? 2 : 1;
      let medVariantId = this.data.recommendedDoseVariantId;
      if (!medVariantId && this.isSwitchingToFoundayo()) {
        medVariantId = Number(FOUNDAYO_08_VARIANT.shopify_variant_id);
      }
      if (!medVariantId) {
        const previousName = this.data.productPurchased || this.data.lastProductName || '';
        const previousFamily = normalizeProductFamily(previousName);
        const targetFamily = resolveTargetMedicationChoice(
          this.data[MEDICATION_CHOICE_KEY],
          previousFamily
        );
        if (targetFamily === 'Foundayo' || previousFamily === 'Foundayo') {
          medVariantId = Number(FOUNDAYO_08_VARIANT.shopify_variant_id);
        }
      }
      if (medVariantId) {
        add(medVariantId, medQty);
      }
      (this.data.optionalExtras || []).forEach((variantId) => add(variantId, 1));

      return lines;
    }

    async addOptionalExtrasToCart() {
      const extras = (this.data.optionalExtras || [])
        .map((id) => Number(id))
        .filter((id) => id > 0);

      if (!extras.length) return;

      await this.clearCart();
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: extras.map((id) => ({ id, quantity: 1 }))
        })
      });
    }

    async setDeliveryDateCartAttribute() {
      if (!this.data.deliveryDate) return;

      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({
            note: `Delivery date:${this.data.deliveryDate}`,
            attributes: {
              'Delivery date': this.data.deliveryDate,
              'Shipping Type': 'weekday'
            }
          })
        });
      } catch (_) {}
    }

    async proceedToShopifyCheckout() {
      const checkoutLines = this.getCheckoutLineItems();

      try {
        await this.clearCart();
      } catch (_) {}

      let checkoutUrl;
      if (checkoutLines.length) {
        checkoutUrl = this.cartPermalinkCheckoutItems(checkoutLines) || this.buildCheckoutUrl();
      } else if ((this.data.optionalExtras || []).length) {
        try {
          await this.addOptionalExtrasToCart();
        } catch (_) {}
        checkoutUrl = this.buildCheckoutUrl();
      } else {
        // Empty /checkout with no cart items redirects to the storefront homepage.
        throw new Error('No checkout line items resolved');
      }

      await this.setDeliveryDateCartAttribute();
      window.location.assign(checkoutUrl);
    }

    submitDeliveryDate() {
      if (!this.validateDeliveryDate()) return;
      void this.confirmDeliveryDateThenComplete();
    }

    async confirmDeliveryDateThenComplete() {
      const confirmed = await this.promptDateConfirmation({
        title: 'Please confirm your delivery date',
        dateLabel: this.formatConfirmDateLabel(this.data.deliveryDate, { weekday: true }),
        hint: 'We will aim for this date for your delivery.'
      });
      if (!confirmed) return;
      await this.complete();
    }

    formatConfirmDateLabel(isoDate, options = {}) {
      const date = new Date(`${isoDate}T00:00:00`);
      if (Number.isNaN(date.getTime())) return String(isoDate || '');
      const format = {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      };
      if (options.weekday) format.weekday = 'long';
      return date.toLocaleDateString('en-GB', format);
    }

    getDateConfirmationPayload() {
      const step = STEPS[this.currentStep];

      if (step === 'last-injection-date' && this.data.lastDoseDateIso) {
        return {
          title: 'Please confirm your last dose date',
          dateLabel: this.formatConfirmDateLabel(this.data.lastDoseDateIso),
          hint: 'This is the date your last dose was administered.'
        };
      }

      if (
        step === 'medication-supply'
        && this.data[MEDICATION_SUPPLY_KEY] === MEDICATION_SUPPLY_TRAVEL
        && this.data.travelFromDateIso
        && this.data.travelToDateIso
      ) {
        return {
          title: 'Please confirm your travel dates',
          dateLabel: `From ${this.formatConfirmDateLabel(this.data.travelFromDateIso)}\nTo ${this.formatConfirmDateLabel(this.data.travelToDateIso)}`,
          hint: 'These dates are used to review your travel medication request.'
        };
      }

      return null;
    }

    promptDateConfirmation({ title, dateLabel, hint }) {
      const modal = this.root.querySelector('[data-date-confirm]');
      if (!modal) return Promise.resolve(true);

      const titleEl = modal.querySelector('[data-date-confirm-title]');
      const dateEl = modal.querySelector('[data-date-confirm-date]');
      const hintEl = modal.querySelector('[data-date-confirm-hint]');
      const okBtn = modal.querySelector('[data-date-confirm-ok]');
      const editBtn = modal.querySelector('[data-date-confirm-edit]');
      const dismissEls = modal.querySelectorAll('[data-date-confirm-dismiss]');

      if (titleEl) titleEl.textContent = title || 'Please confirm this date is correct';
      if (dateEl) dateEl.textContent = dateLabel || '';
      if (hintEl) {
        hintEl.textContent = hint || 'Please check carefully before continuing.';
      }

      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      if (okBtn) okBtn.focus();

      return new Promise((resolve) => {
        const finish = (confirmed) => {
          modal.hidden = true;
          document.body.style.overflow = '';
          okBtn?.removeEventListener('click', onOk);
          editBtn?.removeEventListener('click', onEdit);
          dismissEls.forEach((el) => el.removeEventListener('click', onEdit));
          document.removeEventListener('keydown', onKey);
          resolve(confirmed);
        };

        const onOk = () => finish(true);
        const onEdit = () => finish(false);
        const onKey = (e) => {
          if (e.key === 'Escape') finish(false);
        };

        okBtn?.addEventListener('click', onOk);
        editBtn?.addEventListener('click', onEdit);
        dismissEls.forEach((el) => el.addEventListener('click', onEdit));
        document.addEventListener('keydown', onKey);
      });
    }

    async complete() {
      this.saveData();
      this.clearWbqSubmitError();

      const submitBtn = this.root.querySelector('[data-delivery-submit]');
      const submitBtnText = submitBtn?.textContent;

      try {
        this.showWbqSubmitProgress('Submitting your check-in...');
        const submitted = await this.syncCreateWellbeingToApi();
        if (!submitted) {
          this.hideWbqSubmitProgress();
          this.showWbqSubmitError('We could not submit your check-in. Please try again.');
          if (submitBtn) {
            submitBtn.textContent = submitBtnText || 'Submit';
            this.updateDeliverySubmitState();
          }
          return;
        }

        if (isCheckoutDisabled()) {
          this.hideWbqSubmitProgress();
          this.showWbqSubmitSuccess('Check-in submitted. Checkout is disabled for this preview.');
          return;
        }

        this.showWbqSubmitProgress('Redirecting to checkout...');
        await this.proceedToShopifyCheckout();
      } catch (_) {
        this.hideWbqSubmitProgress();
        this.showWbqSubmitError('We could not continue to checkout. Please try again.');
        if (submitBtn) {
          submitBtn.textContent = submitBtnText || 'Submit';
          this.updateDeliverySubmitState();
        }
      }
    }

    validateMedicationSupply(options = {}) {
      const { showFieldErrors = false } = options;
      const choice = this.data[MEDICATION_SUPPLY_KEY];

      if (!choice) {
        if (showFieldErrors) {
          this.showGroupError(MEDICATION_SUPPLY_KEY, 'Please choose an option.');
        }
        return false;
      }

      if (choice === MEDICATION_SUPPLY_NONE) {
        this.clearTravelFollowup();
        this.saveData();
        return true;
      }

      if (choice !== MEDICATION_SUPPLY_TRAVEL) return false;

      if (!this.validateTravelDates({ showFieldErrors })) return false;

      if (!this.data[TRAVEL_PROOF_UPLOAD_KEY]) {
        if (showFieldErrors) {
          this.showGroupError(TRAVEL_PROOF_UPLOAD_KEY, 'Please upload proof of travel.');
        }
        return false;
      }

      const checkbox = this.root.querySelector('[data-travel-schedule-checkbox]');
      if (!checkbox?.checked) {
        if (showFieldErrors) {
          this.showGroupError('travelScheduleReview', 'Please confirm you understand your dosing and delivery schedule may need review.');
        }
        return false;
      }

      this.data[TRAVEL_SCHEDULE_REVIEW_KEY] = true;
      this.saveData();
      return true;
    }

    validateTravelDates(options = {}) {
      const { showFieldErrors = false, requireThreeWeeks = true } = options;
      TRAVEL_DATE_FIELDS.forEach((name) => this.clearFieldError(name));
      this.clearGroupError('travelDates');

      const fromResult = parseTravelDate(
        this.getFieldValue('travelFromDay'),
        this.getFieldValue('travelFromMonth'),
        this.getFieldValue('travelFromYear'),
        'travelFrom'
      );
      if (!fromResult.valid) {
        if (showFieldErrors && fromResult.field && fromResult.message) {
          this.showFieldError(fromResult.field, fromResult.message);
        }
        return false;
      }

      const toResult = parseTravelDate(
        this.getFieldValue('travelToDay'),
        this.getFieldValue('travelToMonth'),
        this.getFieldValue('travelToYear'),
        'travelTo'
      );
      if (!toResult.valid) {
        if (showFieldErrors && toResult.field && toResult.message) {
          this.showFieldError(toResult.field, toResult.message);
        }
        return false;
      }

      if (toResult.date < fromResult.date) {
        if (showFieldErrors) {
          this.showGroupError('travelDates', 'The end date must be on or after the start date.');
        }
        return false;
      }

      if (requireThreeWeeks) {
        const tripOverThreeWeeks = isTripOverThreeWeeks({
          startDay: fromResult.day,
          startMonth: fromResult.month,
          startYear: fromResult.year,
          endDay: toResult.day,
          endMonth: toResult.month,
          endYear: toResult.year
        });

        if (!tripOverThreeWeeks) {
          if (showFieldErrors) {
            this.showGroupError(
              'travelDates',
              'Travel dates must cover at least 3 weeks (21 days including start and end date).'
            );
          }
          return false;
        }
      }

      this.data.travelFromDay = String(fromResult.day);
      this.data.travelFromMonth = String(fromResult.month);
      this.data.travelFromYear = String(fromResult.year);
      this.data.travelFromDateIso = fromResult.iso;
      this.data.travelToDay = String(toResult.day);
      this.data.travelToMonth = String(toResult.month);
      this.data.travelToYear = String(toResult.year);
      this.data.travelToDateIso = toResult.iso;
      this.saveData();
      return true;
    }

    isTravelDatesComplete() {
      const fromResult = parseTravelDate(
        this.getFieldValue('travelFromDay'),
        this.getFieldValue('travelFromMonth'),
        this.getFieldValue('travelFromYear'),
        'travelFrom'
      );
      const toResult = parseTravelDate(
        this.getFieldValue('travelToDay'),
        this.getFieldValue('travelToMonth'),
        this.getFieldValue('travelToYear'),
        'travelTo'
      );
      if (!fromResult.valid || !toResult.valid || toResult.date < fromResult.date) {
        return false;
      }
      return isTripOverThreeWeeks({
        startDay: fromResult.day,
        startMonth: fromResult.month,
        startYear: fromResult.year,
        endDay: toResult.day,
        endMonth: toResult.month,
        endYear: toResult.year
      });
    }

    isMedicationSupplyComplete() {
      const choice = this.data[MEDICATION_SUPPLY_KEY];
      if (!choice) return false;
      if (choice === MEDICATION_SUPPLY_NONE) return true;
      if (choice !== MEDICATION_SUPPLY_TRAVEL) return false;
      const checkbox = this.root.querySelector('[data-travel-schedule-checkbox]');
      return this.isTravelDatesComplete()
        && !!this.data[TRAVEL_PROOF_UPLOAD_KEY]
        && !!checkbox?.checked;
    }

    clearTravelFollowup() {
      TRAVEL_DATE_FIELDS.forEach((name) => {
        delete this.data[name];
        this.setFieldValue(name, '');
        this.clearFieldError(name);
      });
      delete this.data.travelFromDateIso;
      delete this.data.travelToDateIso;
      delete this.data[TRAVEL_PROOF_UPLOAD_KEY];
      delete this.data.travelProofUploadUuid;
      delete this.data[TRAVEL_SCHEDULE_REVIEW_KEY];
      this.clearGroupError('travelDates');
      this.clearGroupError(TRAVEL_PROOF_UPLOAD_KEY);
      this.clearGroupError('travelScheduleReview');
      if (this.travelUploadcareWidget) this.travelUploadcareWidget.value(null);
      this.updateTravelUploadPreview(null);
      const checkbox = this.root.querySelector('[data-travel-schedule-checkbox]');
      if (checkbox) checkbox.checked = false;
    }

    updateMedicationSupplyDetail() {
      const detail = this.root.querySelector('[data-medication-supply-travel-detail]');
      const showTravel = this.data[MEDICATION_SUPPLY_KEY] === MEDICATION_SUPPLY_TRAVEL;
      if (detail) detail.hidden = !showTravel;

      if (showTravel) {
        this.initTravelProofUpload();
        this.restoreTravelDateFields();
        this.restoreTravelCheckbox();
        if (this.data[TRAVEL_PROOF_UPLOAD_KEY]) {
          this.updateTravelUploadPreviewFromUrl(this.data[TRAVEL_PROOF_UPLOAD_KEY]);
        }
      } else if (this.data[MEDICATION_SUPPLY_KEY] === MEDICATION_SUPPLY_NONE) {
        this.clearTravelFollowup();
      }
    }

    getSelectedMedicationFamily() {
      const previousName = this.data.productPurchased || this.data.lastProductName || '';
      const previousFamily = normalizeProductFamily(previousName);
      return resolveTargetMedicationChoice(this.data[MEDICATION_CHOICE_KEY], previousFamily)
        || previousFamily;
    }

    showsWegovyInjectionAccessories() {
      return isWegovyInjectionAccessoriesFamily(this.getSelectedMedicationFamily());
    }

    showsSharpsBinAccessory() {
      return isInjectableAccessoriesFamily(this.getSelectedMedicationFamily());
    }

    needsDeliveryAccessoriesStep() {
      return this.showsWegovyInjectionAccessories() || this.showsSharpsBinAccessory();
    }

    clearWegovyAccessoriesData() {
      delete this.data[EXTRA_NEEDLES_KEY];
      delete this.data[PENCYCLE_BOX_KEY];
      this.clearGroupError(EXTRA_NEEDLES_KEY);
      this.clearGroupError(PENCYCLE_BOX_KEY);

      [EXTRA_NEEDLES_KEY, PENCYCLE_BOX_KEY].forEach((key) => {
        const group = this.root.querySelector(`[data-selector-group][data-key="${key}"]`);
        if (!group) return;
        group.querySelectorAll('[data-selector]').forEach((el) => el.classList.remove('is-selected'));
      });
    }

    clearSharpsBinData() {
      delete this.data[SHARPS_BIN_KEY];
      this.clearGroupError(SHARPS_BIN_KEY);
      const group = this.root.querySelector(`[data-selector-group][data-key="${SHARPS_BIN_KEY}"]`);
      if (group) {
        group.querySelectorAll('[data-selector]').forEach((el) => el.classList.remove('is-selected'));
      }
    }

    clearDeliveryAccessoriesIfNeeded() {
      if (!this.showsWegovyInjectionAccessories()) {
        this.clearWegovyAccessoriesData();
      }
      if (!this.showsSharpsBinAccessory()) {
        this.clearSharpsBinData();
      }
    }

    updateDeliveryAccessoriesVisibility() {
      const wegovyBlock = this.root.querySelector('[data-wegovy-accessories]');
      const sharpsBlock = this.root.querySelector('[data-sharps-bin-accessories]');
      const showWegovy = this.showsWegovyInjectionAccessories();
      const showSharps = this.showsSharpsBinAccessory();

      if (wegovyBlock) wegovyBlock.hidden = !showWegovy;
      if (sharpsBlock) sharpsBlock.hidden = !showSharps;

      if (!showWegovy) this.clearWegovyAccessoriesData();
      if (!showSharps) this.clearSharpsBinData();

      this.updateContinueState();
    }

    validateDeliveryAccessories(options = {}) {
      const { showFieldErrors = false } = options;
      let valid = true;

      if (this.showsWegovyInjectionAccessories()) {
        if (!this.data[EXTRA_NEEDLES_KEY]) {
          if (showFieldErrors) {
            this.showGroupError(EXTRA_NEEDLES_KEY, 'Please choose an option.');
          }
          valid = false;
        }
        if (!this.data[PENCYCLE_BOX_KEY]) {
          if (showFieldErrors) {
            this.showGroupError(PENCYCLE_BOX_KEY, 'Please choose an option.');
          }
          valid = false;
        }
      }

      if (this.showsSharpsBinAccessory() && !this.data[SHARPS_BIN_KEY]) {
        if (showFieldErrors) {
          this.showGroupError(SHARPS_BIN_KEY, 'Please choose an option.');
        }
        valid = false;
      }

      return valid;
    }

    isDeliveryAccessoriesComplete() {
      if (this.showsSharpsBinAccessory() && !this.data[SHARPS_BIN_KEY]) return false;
      if (!this.showsWegovyInjectionAccessories()) return true;
      return !!(this.data[EXTRA_NEEDLES_KEY] && this.data[PENCYCLE_BOX_KEY]);
    }

    restoreTravelDateFields() {
      TRAVEL_DATE_FIELDS.forEach((name) => {
        if (this.data[name] != null) this.setFieldValue(name, this.data[name]);
      });
    }

    restoreTravelCheckbox() {
      const checkbox = this.root.querySelector('[data-travel-schedule-checkbox]');
      if (checkbox) checkbox.checked = !!this.data[TRAVEL_SCHEDULE_REVIEW_KEY];
    }

    initTravelProofUpload() {
      const input = this.root.querySelector('[data-travel-upload-input]');
      if (!input || this.travelUploadcareWidget) return;

      if (typeof uploadcare === 'undefined') {
        setTimeout(() => this.initTravelProofUpload(), 150);
        return;
      }

      const pubkey = this.root.dataset.uploadcareKey || input.dataset.publicKey;
      if (pubkey) input.setAttribute('data-public-key', pubkey);

      this.travelUploadcareWidget = uploadcare.Widget(input);

      this.travelUploadcareWidget.onChange((file) => {
        if (!file) {
          delete this.data[TRAVEL_PROOF_UPLOAD_KEY];
          delete this.data.travelProofUploadUuid;
          this.updateTravelUploadPreview(null);
          this.saveData();
          this.updateContinueState();
          return;
        }

        file.done((info) => {
          this.data[TRAVEL_PROOF_UPLOAD_KEY] = info.cdnUrl;
          this.data.travelProofUploadUuid = info.uuid;
          this.saveData();
          this.updateTravelUploadPreview(info);
          this.clearGroupError(TRAVEL_PROOF_UPLOAD_KEY);
          this.updateContinueState();
        }).fail(() => {
          delete this.data[TRAVEL_PROOF_UPLOAD_KEY];
          delete this.data.travelProofUploadUuid;
          this.updateTravelUploadPreview(null);
          this.saveData();
          this.updateContinueState();
        });
      });

      if (this.data[TRAVEL_PROOF_UPLOAD_KEY]) {
        this.travelUploadcareWidget.value(this.data[TRAVEL_PROOF_UPLOAD_KEY]);
        this.updateTravelUploadPreviewFromUrl(this.data[TRAVEL_PROOF_UPLOAD_KEY]);
      }
    }

    updateTravelUploadPreview(info) {
      const preview = this.root.querySelector('[data-travel-upload-preview]');
      const img = this.root.querySelector('[data-travel-upload-preview-img]');
      const fileLabel = this.root.querySelector('[data-travel-upload-preview-file]');
      if (!preview || !img || !fileLabel) return;

      if (!info || !info.cdnUrl) {
        preview.hidden = true;
        img.removeAttribute('src');
        img.hidden = false;
        fileLabel.hidden = true;
        fileLabel.textContent = '';
        return;
      }

      preview.hidden = false;
      const isPdf = (info.mimeType && info.mimeType === 'application/pdf')
        || /\.pdf(\?|$)/i.test(info.cdnUrl);

      if (isPdf) {
        img.hidden = true;
        fileLabel.hidden = false;
        fileLabel.textContent = info.name || 'PDF uploaded';
      } else {
        img.hidden = false;
        fileLabel.hidden = true;
        img.src = info.cdnUrl + '-/preview/600x400/';
        img.alt = info.name || 'Uploaded proof of travel';
      }
    }

    updateTravelUploadPreviewFromUrl(url) {
      if (!url) {
        this.updateTravelUploadPreview(null);
        return;
      }
      this.updateTravelUploadPreview({
        cdnUrl: url,
        mimeType: /\.pdf(\?|$)/i.test(url) ? 'application/pdf' : 'image/jpeg',
        name: 'Uploaded file'
      });
    }

    validateNextDosePreference(options = {}) {
      const { showFieldErrors = false } = options;
      const pref = this.data[NEXT_DOSE_PREFERENCE_KEY];

      if (!pref) {
        if (showFieldErrors) {
          this.showGroupError(NEXT_DOSE_PREFERENCE_KEY, this.isSwitchingToTablet()
            ? 'Please choose a dose.'
            : 'Please choose an option.');
        }
        return false;
      }

      if (this.isSwitchingToTablet()) {
        if (this.isEnhancedTabletDoseSelected() && !this.data.enhancedSwitchAcknowledged) {
          if (showFieldErrors) {
            this.showGroupError(
              'enhancedSwitchAck',
              'Please confirm you understand this is an off-label enhanced switch.'
            );
          }
          return false;
        }
        this.saveData();
        return true;
      }

      if (pref === NEXT_DOSE_DECREASE) {
        if (!this.data[NEXT_DOSE_DECREASE_REASON_KEY]) {
          if (showFieldErrors) {
            this.showGroupError(NEXT_DOSE_DECREASE_REASON_KEY, 'Please choose an option.');
          }
          return false;
        }
        const decreaseNotes = this.getFieldValue(NEXT_DOSE_DECREASE_NOTES_FIELD);
        if (decreaseNotes) {
          this.data[NEXT_DOSE_DECREASE_NOTES_FIELD] = decreaseNotes;
        } else {
          delete this.data[NEXT_DOSE_DECREASE_NOTES_FIELD];
        }
      } else {
        delete this.data[NEXT_DOSE_DECREASE_REASON_KEY];
        delete this.data[NEXT_DOSE_DECREASE_NOTES_FIELD];
      }

      if (pref === NEXT_DOSE_STOP) {
        const detail = this.getFieldValue(NEXT_DOSE_STOP_REASON_FIELD);
        if (!detail) {
          if (showFieldErrors) {
            this.markTextareaError(NEXT_DOSE_STOP_REASON_FIELD);
          }
          return false;
        }
        this.data[NEXT_DOSE_STOP_REASON_FIELD] = detail;
      } else {
        delete this.data[NEXT_DOSE_STOP_REASON_FIELD];
      }

      if (pref === NEXT_DOSE_INCREASE || pref === NEXT_DOSE_SAME) {
        const notes = this.getFieldValue(NEXT_DOSE_PRESCRIBER_NOTES_FIELD);
        if (notes) {
          this.data[NEXT_DOSE_PRESCRIBER_NOTES_FIELD] = notes;
        } else {
          delete this.data[NEXT_DOSE_PRESCRIBER_NOTES_FIELD];
        }
      } else {
        delete this.data[NEXT_DOSE_PRESCRIBER_NOTES_FIELD];
      }

      this.saveData();
      return true;
    }

    isNextDosePreferenceComplete() {
      const pref = this.data[NEXT_DOSE_PREFERENCE_KEY];
      if (!pref) return false;

      if (this.isSwitchingToTablet()) {
        if (this.isEnhancedTabletDoseSelected() && !this.data.enhancedSwitchAcknowledged) {
          return false;
        }
        return true;
      }

      if (pref === NEXT_DOSE_DECREASE) {
        return !!this.data[NEXT_DOSE_DECREASE_REASON_KEY];
      }
      if (pref === NEXT_DOSE_STOP) {
        return this.getFieldValue(NEXT_DOSE_STOP_REASON_FIELD).length > 0;
      }
      return true;
    }

    updateEnhancedSwitchUI() {
      const tip = this.root.querySelector('[data-enhanced-switch-tip]');
      const detail = this.root.querySelector('[data-enhanced-switch-detail]');
      const checkbox = this.root.querySelector('[data-enhanced-switch-checkbox]');
      const isPillSwitch = this.isSwitchingToTablet();
      const showEnhanced = this.isEnhancedTabletDoseSelected();

      if (tip) tip.hidden = !isPillSwitch;
      if (detail) detail.hidden = !showEnhanced;

      if (!showEnhanced) {
        this.data.enhancedSwitchAcknowledged = false;
        if (checkbox) checkbox.checked = false;
        this.clearGroupError('enhancedSwitchAck');
      } else if (checkbox) {
        checkbox.checked = !!this.data.enhancedSwitchAcknowledged;
      }
    }

    promptEnhancedSwitchInfo() {
      const modal = this.root.querySelector('[data-enhanced-switch-info]');
      if (!modal) return Promise.resolve();

      const okBtn = modal.querySelector('[data-enhanced-switch-info-ok]');
      const dismissEls = modal.querySelectorAll('[data-enhanced-switch-info-dismiss]');

      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      if (okBtn) okBtn.focus();

      return new Promise((resolve) => {
        const finish = () => {
          modal.hidden = true;
          document.body.style.overflow = '';
          okBtn?.removeEventListener('click', onOk);
          dismissEls.forEach((el) => el.removeEventListener('click', onOk));
          document.removeEventListener('keydown', onKey);
          resolve();
        };

        const onOk = () => finish();
        const onKey = (e) => {
          if (e.key === 'Escape' || e.key === 'Enter') finish();
        };

        okBtn?.addEventListener('click', onOk);
        dismissEls.forEach((el) => el.addEventListener('click', onOk));
        document.addEventListener('keydown', onKey);
      });
    }

    maybeShowEnhancedSwitchInfo() {
      if (STEPS[this.currentStep] !== 'next-dose-preference') return;
      if (!this.hasEnhancedTabletSwitchOptions()) return;
      if (this.data._enhancedSwitchInfoShown) return;
      this.data._enhancedSwitchInfoShown = true;
      this.saveData();
      void this.promptEnhancedSwitchInfo();
    }

    updateNextDosePreferenceOptions() {
      const directionGroup = this.root.querySelector('[data-next-dose-direction-options]');
      const pillGroup = this.root.querySelector('[data-next-dose-pill-switch-options]');
      const heading = this.root.querySelector('[data-next-dose-heading]');
      const subtext = this.root.querySelector('[data-next-dose-pill-switch-subtext]');
      const isPillSwitch = this.isSwitchingToTablet();

      if (heading) {
        heading.textContent = isPillSwitch
          ? 'Which dose would you prefer for your next order?'
          : 'How would you like to proceed with your next dose?';
      }
      if (subtext) subtext.hidden = !isPillSwitch;

      if (directionGroup) directionGroup.hidden = isPillSwitch;
      if (pillGroup) pillGroup.hidden = !isPillSwitch;

      if (isPillSwitch && pillGroup) {
        const options = this.getTabletSwitchDoseOptions();
        const selected = this.data[NEXT_DOSE_PREFERENCE_KEY];

        if (selected && !options.some((option) => option.name === selected)) {
          delete this.data[NEXT_DOSE_PREFERENCE_KEY];
          delete this.data.enhancedSwitchAcknowledged;
        }

        pillGroup.innerHTML = options.map((option) => (
          `<button type="button" class="wbq-form__selector" data-selector data-value="${escapeHtml(option.name)}" data-variant-id="${escapeHtml(option.value)}">${escapeHtml(option.name)}</button>`
        )).join('');

        const active = this.data[NEXT_DOSE_PREFERENCE_KEY];
        if (active) {
          pillGroup.querySelectorAll('[data-selector]').forEach((el) => {
            el.classList.toggle('is-selected', el.dataset.value === active);
          });
        }

        this.updateNextDoseFollowups();
        this.updateEnhancedSwitchUI();
        return;
      }

      if (pillGroup) pillGroup.innerHTML = '';

      const group = directionGroup;
      if (!group) return;

      const bounds = getCurrentDoseBounds(this.data);
      const increaseBtn = group.querySelector(`[data-selector][data-value="${NEXT_DOSE_INCREASE}"]`);
      const decreaseBtn = group.querySelector(`[data-selector][data-value="${NEXT_DOSE_DECREASE}"]`);

      if (increaseBtn) increaseBtn.hidden = !!bounds.atMax;
      if (decreaseBtn) decreaseBtn.hidden = !!bounds.atMin;

      const selected = this.data[NEXT_DOSE_PREFERENCE_KEY];
      if (
        (selected === NEXT_DOSE_INCREASE && bounds.atMax)
        || (selected === NEXT_DOSE_DECREASE && bounds.atMin)
      ) {
        delete this.data[NEXT_DOSE_PREFERENCE_KEY];
        group.querySelectorAll('[data-selector]').forEach((el) => el.classList.remove('is-selected'));
        this.updateNextDoseFollowups();
      }

      this.updateEnhancedSwitchUI();
    }

    updateNextDoseFollowups() {
      if (this.isSwitchingToTablet()) {
        const notesSection = this.root.querySelector('[data-next-dose-prescriber-notes]');
        const decreaseSection = this.root.querySelector('[data-next-dose-decrease-reason]');
        const discussNotice = this.root.querySelector('[data-next-dose-discuss-notice]');
        const stopSection = this.root.querySelector('[data-next-dose-stop-reason]');
        if (notesSection) notesSection.hidden = true;
        if (decreaseSection) decreaseSection.hidden = true;
        if (discussNotice) discussNotice.hidden = true;
        if (stopSection) stopSection.hidden = true;
        this.updateEnhancedSwitchUI();
        return;
      }

      const pref = this.data[NEXT_DOSE_PREFERENCE_KEY];
      const notesSection = this.root.querySelector('[data-next-dose-prescriber-notes]');
      const decreaseSection = this.root.querySelector('[data-next-dose-decrease-reason]');
      const discussNotice = this.root.querySelector('[data-next-dose-discuss-notice]');
      const stopSection = this.root.querySelector('[data-next-dose-stop-reason]');

      const showNotes = pref === NEXT_DOSE_INCREASE || pref === NEXT_DOSE_SAME;
      const showDecrease = pref === NEXT_DOSE_DECREASE;
      const showDiscuss = pref === NEXT_DOSE_DISCUSS;
      const showStop = pref === NEXT_DOSE_STOP;

      if (notesSection) notesSection.hidden = !showNotes;
      if (decreaseSection) decreaseSection.hidden = !showDecrease;
      if (discussNotice) discussNotice.hidden = !showDiscuss;
      if (stopSection) stopSection.hidden = !showStop;

      if (!showNotes) {
        const input = notesSection?.querySelector('[data-wbq-textarea]');
        if (input) {
          input.value = '';
          input.classList.remove('is-error');
        }
        delete this.data[NEXT_DOSE_PRESCRIBER_NOTES_FIELD];
      }

      if (!showDecrease) {
        delete this.data[NEXT_DOSE_DECREASE_REASON_KEY];
        delete this.data[NEXT_DOSE_DECREASE_NOTES_FIELD];
        const group = this.root.querySelector(`[data-selector-group][data-key="${NEXT_DOSE_DECREASE_REASON_KEY}"]`);
        if (group) {
          group.querySelectorAll('[data-selector]').forEach((el) => el.classList.remove('is-selected'));
        }
        this.clearGroupError(NEXT_DOSE_DECREASE_REASON_KEY);
      }

      this.updateNextDoseDecreaseFollowups();

      if (!showStop) {
        const input = stopSection?.querySelector('[data-wbq-textarea]');
        if (input) {
          input.value = '';
          input.classList.remove('is-error');
        }
        delete this.data[NEXT_DOSE_STOP_REASON_FIELD];
      }

      this.saveData();
    }

    updateNextDoseDecreaseFollowups() {
      const reason = this.data[NEXT_DOSE_DECREASE_REASON_KEY];
      const showDecrease = this.data[NEXT_DOSE_PREFERENCE_KEY] === NEXT_DOSE_DECREASE;
      const sideEffectsNotice = this.root.querySelector('[data-next-dose-decrease-side-effects-notice]');
      const goalWeightNotice = this.root.querySelector('[data-next-dose-decrease-goal-weight-notice]');
      const notesSection = this.root.querySelector('[data-next-dose-decrease-notes]');

      if (sideEffectsNotice) {
        sideEffectsNotice.hidden = !showDecrease || reason !== NEXT_DOSE_DECREASE_SIDE_EFFECTS;
      }
      if (goalWeightNotice) {
        goalWeightNotice.hidden = !showDecrease || reason !== NEXT_DOSE_DECREASE_GOAL_WEIGHT;
      }
      if (notesSection) {
        notesSection.hidden = !showDecrease || !reason;
      }

      if (!showDecrease || !reason) {
        const input = notesSection?.querySelector('[data-wbq-textarea]');
        if (input) {
          input.value = '';
          input.classList.remove('is-error');
        }
        delete this.data[NEXT_DOSE_DECREASE_NOTES_FIELD];
      }
    }

    updateMedicationChoiceOptions() {
      const group = this.root.querySelector('[data-selector-group][data-key="medicationChoice"]');
      if (!group) return;

      const options = getMedicationChoiceOptions(this.data, this.forceMedKey);
      const selected = this.data[MEDICATION_CHOICE_KEY];

      if (selected && !options.includes(selected)) {
        delete this.data[MEDICATION_CHOICE_KEY];
      }

      group.innerHTML = options.map((option) => (
        `<button type="button" class="wbq-form__selector" data-selector data-value="${escapeHtml(option)}">${escapeHtml(option)}</button>`
      )).join('');

      if (this.data[MEDICATION_CHOICE_KEY]) {
        group.querySelectorAll('[data-selector]').forEach((el) => {
          el.classList.toggle('is-selected', el.dataset.value === this.data[MEDICATION_CHOICE_KEY]);
        });
      }

      this.updateContinueState();
    }

    validateHealthChanges(options = {}) {
      const { showFieldErrors = false } = options;
      const value = this.data[HEALTH_CHANGES_KEY];

      if (!value) return false;

      if (value === 'yes') {
        const detail = this.getFieldValue(HEALTH_CHANGES_DETAIL_FIELD);
        if (!detail) {
          if (showFieldErrors) {
            this.markTextareaError(HEALTH_CHANGES_DETAIL_FIELD);
          }
          return false;
        }
        this.data[HEALTH_CHANGES_DETAIL_FIELD] = detail;
        this.saveData();
        return true;
      }

      delete this.data[HEALTH_CHANGES_DETAIL_FIELD];
      this.saveData();
      return true;
    }

    isHealthChangesComplete() {
      const value = this.data[HEALTH_CHANGES_KEY];
      if (!value) return false;
      if (value === 'yes') {
        return this.getFieldValue(HEALTH_CHANGES_DETAIL_FIELD).length > 0;
      }
      return true;
    }

    validateWeightLossMethods(options = {}) {
      const { showFieldErrors = false } = options;
      const values = this.data[WEIGHT_LOSS_METHODS_KEY] || [];

      if (!values.length) {
        if (showFieldErrors) {
          this.showGroupError(WEIGHT_LOSS_METHODS_KEY, 'Please choose an option.');
        }
        return false;
      }

      if (values.includes(WEIGHT_LOSS_METHODS_OTHER_VALUE)) {
        const detail = this.getFieldValue(WEIGHT_LOSS_METHODS_OTHER_FIELD);
        if (!detail) {
          if (showFieldErrors) {
            this.markTextareaError(WEIGHT_LOSS_METHODS_OTHER_FIELD);
          }
          return false;
        }
        this.data[WEIGHT_LOSS_METHODS_OTHER_FIELD] = detail;
      } else {
        delete this.data[WEIGHT_LOSS_METHODS_OTHER_FIELD];
      }

      this.saveData();
      return true;
    }

    isWeightLossMethodsComplete() {
      const values = this.data[WEIGHT_LOSS_METHODS_KEY] || [];
      if (!values.length) return false;
      if (values.includes(WEIGHT_LOSS_METHODS_OTHER_VALUE)) {
        return this.getFieldValue(WEIGHT_LOSS_METHODS_OTHER_FIELD).length > 0;
      }
      return true;
    }

    validateSideEffects(options = {}) {
      const { showFieldErrors = false } = options;
      const values = this.data[SIDE_EFFECTS_KEY] || [];

      if (!values.length) {
        if (showFieldErrors) {
          this.showGroupError(SIDE_EFFECTS_KEY, 'Please choose an option.');
        }
        return false;
      }

      if (values.includes(SIDE_EFFECTS_OTHER_VALUE)) {
        const detail = this.getFieldValue(SIDE_EFFECTS_OTHER_FIELD);
        if (!detail) {
          if (showFieldErrors) {
            this.markTextareaError(SIDE_EFFECTS_OTHER_FIELD);
          }
          return false;
        }
        this.data[SIDE_EFFECTS_OTHER_FIELD] = detail;
      } else {
        delete this.data[SIDE_EFFECTS_OTHER_FIELD];
      }

      if (this.hasSelectedSideEffects()) {
        const impact = this.data[SIDE_EFFECTS_IMPACT_KEY] || [];
        if (!impact.length) {
          if (showFieldErrors) {
            this.showGroupError(SIDE_EFFECTS_IMPACT_KEY, 'Please choose an option.');
          }
          return false;
        }

        const management = this.getFieldValue(SIDE_EFFECTS_MANAGEMENT_FIELD);
        if (!management) {
          if (showFieldErrors) {
            this.markTextareaError(SIDE_EFFECTS_MANAGEMENT_FIELD);
          }
          return false;
        }
        this.data[SIDE_EFFECTS_MANAGEMENT_FIELD] = management;
      } else {
        delete this.data[SIDE_EFFECTS_IMPACT_KEY];
        delete this.data[SIDE_EFFECTS_MANAGEMENT_FIELD];
      }

      this.saveData();
      return true;
    }

    isSideEffectsComplete() {
      const values = this.data[SIDE_EFFECTS_KEY] || [];
      if (!values.length) return false;
      if (values.includes(SIDE_EFFECTS_OTHER_VALUE)) {
        if (!this.getFieldValue(SIDE_EFFECTS_OTHER_FIELD).length) return false;
      }
      if (this.hasSelectedSideEffects()) {
        const impact = this.data[SIDE_EFFECTS_IMPACT_KEY] || [];
        if (!impact.length) return false;
        if (!this.getFieldValue(SIDE_EFFECTS_MANAGEMENT_FIELD).length) return false;
      }
      return true;
    }

    showGroupError(key, message) {
      const errorEl = this.root.querySelector(`[data-group-error="${key}"]`);
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.classList.add('is-visible');
      }
      return false;
    }

    clearGroupError(key) {
      const errorEl = this.root.querySelector(`[data-group-error="${key}"]`);
      if (errorEl) {
        errorEl.textContent = '';
        errorEl.classList.remove('is-visible');
      }
    }

    validateCurrentWeight(options = {}) {
      const { showFieldErrors = false } = options;
      if (!this.validateCurrentWeightFields(true, showFieldErrors)) return false;

      this.data.currentStones = this.getFieldValue('currentStones');
      this.data.currentPounds = this.getFieldValue('currentPounds');
      this.data.currentKg = this.getFieldValue('currentKg');
      this.data.currentWeightKg = this.parseWeightKg('current');
      this.saveData();
      return true;
    }

    validateCurrentWeightFields(required, showFieldErrors) {
      const unit = this.data.unitSystem || 'imperial';
      const range = BODY_MEASUREMENT_RANGES.WEIGHT_KG;
      const prefix = 'current';

      if (unit === 'metric') {
        const kg = parseNumericField(this.getFieldValue(`${prefix}Kg`));
        if (kg.empty) {
          if (required && showFieldErrors) {
            this.showRangeError(`${prefix}Kg`, 'Please enter your weight.');
          }
          return !required;
        }
        if (!kg.valid || kg.value < range.min || kg.value > range.max) {
          if (showFieldErrors) {
            this.showRangeError(`${prefix}Kg`, range.message);
          }
          return false;
        }
        this.hideRangeError(`${prefix}Kg`);
        return true;
      }

      const st = parseNumericField(this.getFieldValue(`${prefix}Stones`));
      const lbRaw = this.getFieldValue(`${prefix}Pounds`);
      const lb = lbRaw ? parseNumericField(lbRaw) : { empty: true, valid: true, value: 0 };

      if (st.empty && lb.empty) {
        this.clearFieldError(`${prefix}Pounds`);
        if (required && showFieldErrors) {
          this.showFieldError(`${prefix}Stones`, 'Please enter your weight.');
        }
        return !required;
      }

      if (st.empty || !st.valid || st.value < 0) {
        if (showFieldErrors) {
          this.showFieldError(`${prefix}Stones`, 'Please enter a valid weight in stones.');
        }
        return false;
      }
      if (!lb.empty && (!lb.valid || lb.value < 0 || lb.value >= BODY_MEASUREMENT_RANGES.POUNDS_MAX)) {
        if (showFieldErrors) {
          this.showFieldError(`${prefix}Pounds`, 'Pounds must be between 0 and 13.');
        }
        return false;
      }

      this.clearFieldError(`${prefix}Pounds`);

      const totalLb = (st.value * 14) + (lb.value || 0);
      const kg = Math.round(totalLb * 0.453592 * 10) / 10;
      if (kg < range.min || kg > range.max) {
        if (showFieldErrors) {
          this.showFieldError(`${prefix}Stones`, BODY_MEASUREMENT_RANGES.WEIGHT_IMPERIAL_MESSAGE);
        }
        return false;
      }

      this.clearFieldError(`${prefix}Stones`);
      return true;
    }

    validateCurrentWeightField(name) {
      if (STEPS[this.currentStep] !== 'current-weight') return;
      if (!['currentStones', 'currentPounds', 'currentKg'].includes(name)) return;
      this.validateCurrentWeightFields(true, true);
    }

    isCurrentWeightComplete() {
      const unit = this.data.unitSystem || 'imperial';
      if (unit === 'imperial') {
        return !!this.getFieldValue('currentStones');
      }
      return !!this.getFieldValue('currentKg');
    }

    parseWeightKg(prefix) {
      const unit = this.data.unitSystem || 'imperial';
      if (unit === 'metric') {
        const kg = parseFloat(this.getFieldValue(`${prefix}Kg`));
        return Number.isFinite(kg) ? kg : 0;
      }

      const stones = parseFloat(this.getFieldValue(`${prefix}Stones`)) || 0;
      const pounds = parseFloat(this.getFieldValue(`${prefix}Pounds`)) || 0;
      const totalLb = (stones * 14) + pounds;
      return totalLb > 0 ? Math.round(totalLb * 0.453592 * 10) / 10 : 0;
    }

    handleUnitToggle(btn) {
      const unit = btn.dataset.unit;
      this.data.unitSystem = unit;

      this.root.querySelectorAll('[data-unit-toggle]').forEach((el) => {
        el.classList.toggle('is-active', el.dataset.unit === unit);
      });

      this.root.querySelectorAll('[data-metric-fields]').forEach((el) => {
        el.hidden = unit !== 'metric';
      });
      this.root.querySelectorAll('[data-imperial-fields]').forEach((el) => {
        el.hidden = unit !== 'imperial';
      });

      this.hideCurrentWeightErrors();
      this.saveData();
      this.updateContinueState();
    }

    restoreMeasurementFields() {
      if (this.data.unitSystem) {
        const toggle = this.root.querySelector(`[data-unit-toggle][data-unit="${this.data.unitSystem}"]`);
        if (toggle) this.handleUnitToggle(toggle);
      } else {
        this.data.unitSystem = 'imperial';
        const defaultToggle = this.root.querySelector('[data-unit-toggle][data-unit="imperial"]');
        if (defaultToggle) this.handleUnitToggle(defaultToggle);
      }

      CURRENT_WEIGHT_FIELDS.forEach((name) => {
        if (this.data[name] != null) {
          this.setFieldValue(name, this.data[name]);
        }
      });
    }

    showRangeError(key, message) {
      this.clearFieldError(key);
      const banner = this.root.querySelector(`[data-range-error="${key}"]`);
      if (banner) {
        const text = banner.querySelector('[data-range-error-text]');
        if (text) text.textContent = message;
        banner.classList.add('is-visible');
      }
      const field = this.root.querySelector(`[name="${key}"]`);
      if (field) field.classList.add('is-error');
      return false;
    }

    hideRangeError(key) {
      const banner = this.root.querySelector(`[data-range-error="${key}"]`);
      if (banner) banner.classList.remove('is-visible');
      this.clearFieldError(key);
    }

    hideCurrentWeightErrors() {
      this.hideRangeError('currentKg');
      ['currentStones', 'currentPounds'].forEach((name) => this.clearFieldError(name));
    }

    validateInjectionPenIssues(options = {}) {
      const { showFieldErrors = false } = options;
      const value = this.data[INJECTION_PEN_ISSUES_KEY];

      if (!value) return false;

      if (value === 'yes') {
        const detail = this.getFieldValue(INJECTION_PEN_ISSUES_DETAIL_FIELD);
        if (!detail) {
          if (showFieldErrors) {
            this.markTextareaError(INJECTION_PEN_ISSUES_DETAIL_FIELD);
          }
          return false;
        }
        this.data[INJECTION_PEN_ISSUES_DETAIL_FIELD] = detail;
        this.saveData();
        return true;
      }

      delete this.data[INJECTION_PEN_ISSUES_DETAIL_FIELD];
      this.saveData();
      return true;
    }

    isInjectionPenIssuesComplete() {
      const value = this.data[INJECTION_PEN_ISSUES_KEY];
      if (!value) return false;
      if (value === 'yes') {
        return this.getFieldValue(INJECTION_PEN_ISSUES_DETAIL_FIELD).length > 0;
      }
      return true;
    }

    markTextareaError(name) {
      const field = this.root.querySelector(`[name="${name}"]`);
      if (field) field.classList.add('is-error');
    }

    handleSelector(btn) {
      const group = btn.closest('[data-selector-group]');
      if (!group) return;

      const key = group.dataset.key;
      const value = btn.dataset.value;
      if (!key || !value) return;

      const isMulti = group.dataset.multi === 'true';

      if (isMulti) {
        const noneValue = group.dataset.noneValue;
        const selected = this.data[key] || [];

        if (value === noneValue) {
          this.data[key] = [value];
          group.querySelectorAll('[data-selector]').forEach((el) => {
            el.classList.toggle('is-selected', el.dataset.value === value);
          });
        } else {
          let next = selected.filter((v) => v !== noneValue);
          if (next.includes(value)) {
            next = next.filter((v) => v !== value);
          } else {
            next.push(value);
          }
          this.data[key] = next;
          group.querySelectorAll('[data-selector]').forEach((el) => {
            const v = el.dataset.value;
            if (v === noneValue) {
              el.classList.remove('is-selected');
            } else {
              el.classList.toggle('is-selected', next.includes(v));
            }
          });
        }

        this.clearGroupError(key);

        if (key === SIDE_EFFECTS_KEY) {
          this.updateSideEffectsHelpers();
          this.updateSideEffectsOtherDetail();
          this.updateSideEffectsFollowup();
          if (value === SIDE_EFFECTS_OTHER_VALUE && (this.data[key] || []).includes(SIDE_EFFECTS_OTHER_VALUE)) {
            requestAnimationFrame(() => {
              const detail = this.root.querySelector('[data-side-effects-other-detail]');
              const input = detail?.querySelector('[data-wbq-textarea]');
              if (input && !detail.hidden) input.focus();
            });
          }
        }

        if (key === WEIGHT_LOSS_METHODS_KEY) {
          this.updateWeightLossMethodsOtherDetail();
          this.updateWeightLossMethodsNotice();
          if (value === WEIGHT_LOSS_METHODS_OTHER_VALUE && (this.data[key] || []).includes(WEIGHT_LOSS_METHODS_OTHER_VALUE)) {
            requestAnimationFrame(() => {
              const detail = this.root.querySelector('[data-weight-loss-methods-other-detail]');
              const input = detail?.querySelector('[data-wbq-textarea]');
              if (input && !detail.hidden) input.focus();
            });
          }
        }
      } else {
        this.data[key] = value;
        group.querySelectorAll('[data-selector]').forEach((el) => {
          el.classList.toggle('is-selected', el.dataset.value === value);
        });
        this.clearGroupError(key);

        if (key === INJECTION_TWICE_KEY) {
          this.updateInjectionTwiceNotice();
        }

        if (key === INJECTION_PEN_ISSUES_KEY) {
          this.updateInjectionPenIssuesDetail();
          if (value === 'yes') {
            requestAnimationFrame(() => {
              const detail = this.root.querySelector('[data-injection-pen-issues-detail]');
              const input = detail?.querySelector('[data-wbq-textarea]');
              if (input && !detail.hidden) input.focus();
            });
          }
        }

        if (key === HEALTH_CHANGES_KEY) {
          this.updateHealthChangesDetail();
          if (value === 'yes') {
            requestAnimationFrame(() => {
              const detail = this.root.querySelector('[data-health-changes-detail]');
              const input = detail?.querySelector('[data-wbq-textarea]');
              if (input && !detail.hidden) input.focus();
            });
          }
        }

        if (key === MEDICATION_REGULARITY_KEY) {
          this.updateMedicationRegularityNotice();
        }

        if (key === PILL_MISSED_DURATION_KEY) {
          this.updatePillStoppedDetail();
          if (value === PILL_STOPPED_REASON_TRIGGER) {
            requestAnimationFrame(() => {
              const detail = this.root.querySelector('[data-pill-stopped-detail]');
              const input = detail?.querySelector('[data-wbq-textarea]');
              if (input && !detail.hidden) input.focus();
            });
          }
        }

        if (key === NEXT_DOSE_PREFERENCE_KEY) {
          if (this.isSwitchingToTablet()) {
            if (!this.isEnhancedTabletDoseSelected()) {
              this.data.enhancedSwitchAcknowledged = false;
              const checkbox = this.root.querySelector('[data-enhanced-switch-checkbox]');
              if (checkbox) checkbox.checked = false;
            }
            this.updateEnhancedSwitchUI();
          }
          this.updateNextDoseFollowups();
          this.updateRecommendedDose();
          this.ensureCurrentStepAvailable();
          if (value === NEXT_DOSE_STOP) {
            requestAnimationFrame(() => {
              const detail = this.root.querySelector('[data-next-dose-stop-reason]');
              const input = detail?.querySelector('[data-wbq-textarea]');
              if (input && !detail.hidden) input.focus();
            });
          }
        }

        if (key === MEDICATION_CHOICE_KEY) {
          if (isMedicationSwitchChoice(value)) {
            this.clearNextDosePreferenceData();
            this.updateNextDoseFollowups();
          }
          delete this.data._enhancedSwitchInfoShown;
          this.clearDeliveryAccessoriesIfNeeded();
          this.updateRecommendedDose();
          this.ensureCurrentStepAvailable();
        }

        if (key === NEXT_DOSE_DECREASE_REASON_KEY) {
          this.updateNextDoseDecreaseFollowups();
          this.updateContinueState();
        }

        if (key === MEDICATION_SUPPLY_KEY) {
          this.updateMedicationSupplyDetail();
        }
      }

      this.saveData();
      this.updateContinueState();
    }

    restoreMultiSelectGroup(key) {
      const selected = this.data[key] || [];
      const group = this.root.querySelector(`[data-selector-group][data-key="${key}"]`);
      if (!group) return;

      group.querySelectorAll('[data-selector]').forEach((el) => {
        el.classList.toggle('is-selected', selected.includes(el.dataset.value));
      });
    }

    restoreTextFields() {
      if (this.data[INJECTION_PEN_ISSUES_DETAIL_FIELD] != null) {
        this.setFieldValue(INJECTION_PEN_ISSUES_DETAIL_FIELD, this.data[INJECTION_PEN_ISSUES_DETAIL_FIELD]);
      }
      if (this.data[SIDE_EFFECTS_OTHER_FIELD] != null) {
        this.setFieldValue(SIDE_EFFECTS_OTHER_FIELD, this.data[SIDE_EFFECTS_OTHER_FIELD]);
      }
      if (this.data[SIDE_EFFECTS_MANAGEMENT_FIELD] != null) {
        this.setFieldValue(SIDE_EFFECTS_MANAGEMENT_FIELD, this.data[SIDE_EFFECTS_MANAGEMENT_FIELD]);
      }
      if (this.data[WEIGHT_LOSS_METHODS_OTHER_FIELD] != null) {
        this.setFieldValue(WEIGHT_LOSS_METHODS_OTHER_FIELD, this.data[WEIGHT_LOSS_METHODS_OTHER_FIELD]);
      }
      if (this.data[HEALTH_CHANGES_DETAIL_FIELD] != null) {
        this.setFieldValue(HEALTH_CHANGES_DETAIL_FIELD, this.data[HEALTH_CHANGES_DETAIL_FIELD]);
      }
      if (this.data[PILL_STOPPED_REASON_FIELD] != null) {
        this.setFieldValue(PILL_STOPPED_REASON_FIELD, this.data[PILL_STOPPED_REASON_FIELD]);
      }
      if (this.data[NEXT_DOSE_PRESCRIBER_NOTES_FIELD] != null) {
        this.setFieldValue(NEXT_DOSE_PRESCRIBER_NOTES_FIELD, this.data[NEXT_DOSE_PRESCRIBER_NOTES_FIELD]);
      }
      if (this.data[NEXT_DOSE_DECREASE_NOTES_FIELD] != null) {
        this.setFieldValue(NEXT_DOSE_DECREASE_NOTES_FIELD, this.data[NEXT_DOSE_DECREASE_NOTES_FIELD]);
      }
      if (this.data[NEXT_DOSE_STOP_REASON_FIELD] != null) {
        this.setFieldValue(NEXT_DOSE_STOP_REASON_FIELD, this.data[NEXT_DOSE_STOP_REASON_FIELD]);
      }
    }

    updateHealthChangesDetail() {
      const detailSection = this.root.querySelector('[data-health-changes-detail]');
      const notice = this.root.querySelector('[data-health-changes-notice]');
      const showDetail = this.data[HEALTH_CHANGES_KEY] === 'yes';

      if (notice) notice.hidden = !showDetail;
      if (!detailSection) return;

      detailSection.hidden = !showDetail;

      if (!showDetail) {
        const input = detailSection.querySelector('[data-wbq-textarea]');
        if (input) {
          input.value = '';
          input.classList.remove('is-error');
        }
        delete this.data[HEALTH_CHANGES_DETAIL_FIELD];
        this.saveData();
      }
    }

    updateWeightLossMethodsOtherDetail() {
      const detailSection = this.root.querySelector('[data-weight-loss-methods-other-detail]');
      if (!detailSection) return;

      const selected = this.data[WEIGHT_LOSS_METHODS_KEY] || [];
      const showDetail = selected.includes(WEIGHT_LOSS_METHODS_OTHER_VALUE);
      detailSection.hidden = !showDetail;

      if (!showDetail) {
        const input = detailSection.querySelector('[data-wbq-textarea]');
        if (input) {
          input.value = '';
          input.classList.remove('is-error');
        }
        delete this.data[WEIGHT_LOSS_METHODS_OTHER_FIELD];
        this.saveData();
      }
    }

    updateWeightLossMethodsNotice() {
      const notice = this.root.querySelector('[data-weight-loss-methods-notice]');
      if (!notice) return;
      const selected = this.data[WEIGHT_LOSS_METHODS_KEY] || [];
      notice.hidden = !selected.includes(WEIGHT_LOSS_METHODS_NONE_VALUE);
    }

    hasSelectedSideEffects() {
      const values = this.data[SIDE_EFFECTS_KEY] || [];
      return values.some((value) => value && value !== SIDE_EFFECTS_NONE_VALUE);
    }

    updateSideEffectsFollowup() {
      const followup = this.root.querySelector('[data-side-effects-followup]');
      if (!followup) return;

      const show = this.hasSelectedSideEffects();
      followup.hidden = !show;

      if (!show) {
        delete this.data[SIDE_EFFECTS_IMPACT_KEY];
        delete this.data[SIDE_EFFECTS_MANAGEMENT_FIELD];
        const group = this.root.querySelector(`[data-selector-group][data-key="${SIDE_EFFECTS_IMPACT_KEY}"]`);
        group?.querySelectorAll('[data-selector]').forEach((el) => el.classList.remove('is-selected'));
        const management = followup.querySelector(`[name="${SIDE_EFFECTS_MANAGEMENT_FIELD}"]`);
        if (management) {
          management.value = '';
          management.classList.remove('is-error');
        }
        this.clearGroupError(SIDE_EFFECTS_IMPACT_KEY);
        this.saveData();
      }
    }

    updateSideEffectsHelpers() {
      const selected = this.data[SIDE_EFFECTS_KEY] || [];
      this.root.querySelectorAll('[data-side-effect-helper]').forEach((helper) => {
        const value = helper.getAttribute('data-side-effect-helper');
        helper.hidden = !selected.includes(value);
      });
    }

    updateSideEffectsOtherDetail() {
      const detailSection = this.root.querySelector('[data-side-effects-other-detail]');
      if (!detailSection) return;

      const selected = this.data[SIDE_EFFECTS_KEY] || [];
      const showDetail = selected.includes(SIDE_EFFECTS_OTHER_VALUE);
      detailSection.hidden = !showDetail;

      if (!showDetail) {
        const input = detailSection.querySelector('[data-wbq-textarea]');
        if (input) {
          input.value = '';
          input.classList.remove('is-error');
        }
        delete this.data[SIDE_EFFECTS_OTHER_FIELD];
        this.saveData();
      }
    }

    updateInjectionPenIssuesDetail() {
      const detailSection = this.root.querySelector('[data-injection-pen-issues-detail]');
      if (!detailSection) return;

      const showDetail = this.data[INJECTION_PEN_ISSUES_KEY] === 'yes';
      detailSection.hidden = !showDetail;

      if (!showDetail) {
        const input = detailSection.querySelector('[data-wbq-textarea]');
        if (input) {
          input.value = '';
          input.classList.remove('is-error');
        }
        delete this.data[INJECTION_PEN_ISSUES_DETAIL_FIELD];
        this.saveData();
      }
    }

    restoreSelectorGroup(key) {
      const group = this.root.querySelector(`[data-selector-group][data-key="${key}"]`);
      if (!group) return;

      if (group.dataset.multi === 'true') {
        this.restoreMultiSelectGroup(key);
        return;
      }

      const value = this.data[key];
      group.querySelectorAll('[data-selector]').forEach((el) => {
        el.classList.toggle('is-selected', el.dataset.value === value);
      });
    }

    restoreSelectorGroups() {
      this.restoreSelectorGroup(INJECTION_TWICE_KEY);
      this.updateInjectionTwiceNotice();
      this.restoreSelectorGroup(INJECTION_PEN_ISSUES_KEY);
      this.restoreSelectorGroup(SIDE_EFFECTS_KEY);
      this.restoreSelectorGroup(SIDE_EFFECTS_IMPACT_KEY);
      this.restoreSelectorGroup(MEDICATION_REGULARITY_KEY);
      this.restoreSelectorGroup(PILL_MISSED_DURATION_KEY);
      this.restoreSelectorGroup(PILL_TABLET_ADHERENCE_KEY);
      this.restoreSelectorGroup(WEIGHT_LOSS_METHODS_KEY);
      this.restoreSelectorGroup(HEALTH_CHANGES_KEY);
      this.restoreSelectorGroup(NEXT_DOSE_PREFERENCE_KEY);
      this.restoreSelectorGroup(NEXT_DOSE_DECREASE_REASON_KEY);
      this.restoreSelectorGroup(MEDICATION_SUPPLY_KEY);
      this.restoreSelectorGroup(SHARPS_BIN_KEY);
      this.updateMedicationChoiceOptions();
      this.restoreTextFields();
      this.updateInjectionPenIssuesDetail();
      this.updateSideEffectsHelpers();
      this.updateSideEffectsOtherDetail();
      this.updateSideEffectsFollowup();
      this.updateMedicationRegularityNotice();
      this.updatePillStoppedDetail();
      this.updateWeightLossMethodsOtherDetail();
      this.updateWeightLossMethodsNotice();
      this.updateHealthChangesDetail();
      this.updateNextDosePreferenceOptions();
      this.updateNextDoseFollowups();
      this.updateMedicationSupplyDetail();
    }

    updateMedicationRegularityNotice() {
      const notice = this.root.querySelector('[data-medication-regularity-notice]');
      if (!notice) return;
      notice.hidden = this.data[MEDICATION_REGULARITY_KEY] !== MEDICATION_REGULARITY_SKIPPED_TWO_OR_MORE;
    }

    updatePillStoppedDetail() {
      const detailSection = this.root.querySelector('[data-pill-stopped-detail]');
      const notice = this.root.querySelector('[data-pill-stopped-notice]');
      const missed = this.data[PILL_MISSED_DURATION_KEY];
      const showPermanentStop = missed === PILL_STOPPED_REASON_TRIGGER;

      if (notice) notice.hidden = !showPermanentStop;
      if (!detailSection) return;

      detailSection.hidden = !showPermanentStop;

      if (!showPermanentStop) {
        const input = detailSection.querySelector('[data-wbq-textarea]');
        if (input) {
          input.value = '';
          input.classList.remove('is-error');
        }
        delete this.data[PILL_STOPPED_REASON_FIELD];
        this.saveData();
      }
    }

    isPillMissedDurationComplete() {
      const missed = this.data[PILL_MISSED_DURATION_KEY];
      if (!missed) return false;
      if (missed === PILL_STOPPED_REASON_TRIGGER) {
        return this.getFieldValue(PILL_STOPPED_REASON_FIELD).length > 0;
      }
      return true;
    }

    isPillTabletAdherenceComplete() {
      return !!this.data[PILL_TABLET_ADHERENCE_KEY];
    }

    validatePillMissedDuration({ showFieldErrors } = {}) {
      if (!this.data[PILL_MISSED_DURATION_KEY]) {
        if (showFieldErrors) {
          this.showGroupError(PILL_MISSED_DURATION_KEY, 'Please choose an option.');
        }
        return false;
      }

      if (this.data[PILL_MISSED_DURATION_KEY] === PILL_STOPPED_REASON_TRIGGER) {
        const detail = this.getFieldValue(PILL_STOPPED_REASON_FIELD);
        if (!detail) {
          if (showFieldErrors) this.markTextareaError(PILL_STOPPED_REASON_FIELD);
          return false;
        }
        this.data[PILL_STOPPED_REASON_FIELD] = detail;
      } else {
        delete this.data[PILL_STOPPED_REASON_FIELD];
      }

      this.saveData();
      return true;
    }

    validatePillTabletAdherence({ showFieldErrors } = {}) {
      if (!this.data[PILL_TABLET_ADHERENCE_KEY]) {
        if (showFieldErrors) {
          this.showGroupError(PILL_TABLET_ADHERENCE_KEY, 'Please choose an option.');
        }
        return false;
      }

      this.saveData();
      return true;
    }

    updateInjectionTwiceNotice() {
      const notice = this.root.querySelector('[data-injection-twice-notice]');
      if (!notice) return;
      notice.hidden = this.data[INJECTION_TWICE_KEY] !== 'no';
    }

    getActiveStepEl() {
      return this.root.querySelector('[data-step].is-active');
    }

    updateProgress() {
      if (!this.progressFill) return;
      const step = STEPS[this.currentStep];
      const visible = this.getVisibleStepIndexes().filter((i) => STEPS[i] !== 'consultation-needed');
      const total = visible.length;
      const position = visible.indexOf(this.currentStep);
      const current = position >= 0 ? position : 0;
      const pct = total <= 1 ? 0 : Math.round((current / (total - 1)) * 100);
      this.progressFill.style.width = pct + '%';
      if (this.navEl) {
        this.navEl.hidden = total <= 1 || step === 'consultation-needed';
      }
    }

    updateContinueState() {
      const activeStep = this.getActiveStepEl();
      const btn = activeStep?.querySelector('[data-wbq-continue]');
      if (!btn) return;

      const step = STEPS[this.currentStep];
      let enabled = true;

      if (step === 'last-injection-date') {
        enabled = this.isLastInjectionDateComplete();
      } else if (step === 'injection-twice') {
        enabled = this.data[INJECTION_TWICE_KEY] === 'yes';
      } else if (step === 'injection-pen-issues') {
        enabled = this.isInjectionPenIssuesComplete();
      } else if (step === 'current-weight') {
        enabled = this.isCurrentWeightComplete();
      } else if (step === 'side-effects') {
        enabled = this.isSideEffectsComplete();
      } else if (step === 'medication-regularity') {
        enabled = !!this.data[MEDICATION_REGULARITY_KEY];
      } else if (step === 'pill-adherence') {
        enabled = this.isPillMissedDurationComplete();
      } else if (step === 'pill-tablet-adherence') {
        enabled = this.isPillTabletAdherenceComplete();
      } else if (step === 'weight-loss-methods') {
        enabled = this.isWeightLossMethodsComplete();
      } else if (step === 'health-changes') {
        enabled = this.isHealthChangesComplete();
      } else if (step === 'medication-choice') {
        enabled = !!this.data[MEDICATION_CHOICE_KEY];
      } else if (step === 'next-dose-preference') {
        enabled = this.isNextDosePreferenceComplete();
      } else if (step === 'medication-supply') {
        enabled = this.isMedicationSupplyComplete();
      } else if (step === 'delivery-accessories') {
        enabled = this.isDeliveryAccessoriesComplete();
      } else if (step === 'delivery-date') {
        enabled = !!this.data.deliveryDate;
      }

      btn.classList.toggle('is-disabled', !enabled);
      btn.disabled = !enabled;
    }

    goToStep(index, animate) {
      if (index < 0 || index >= STEPS.length) return;

      const resolvedIndex = this.isStepAvailable(STEPS[index])
        ? index
        : this.resolveAvailableStepIndex(index, 1);
      if (resolvedIndex !== index && !this.isStepAvailable(STEPS[index])) {
        this.goToStep(resolvedIndex, animate);
        return;
      }

      const stepName = STEPS[resolvedIndex];
      const steps = this.root.querySelectorAll('[data-step]');

      steps.forEach((el) => {
        const isTarget = el.dataset.step === stepName;
        el.classList.toggle('is-active', isTarget);
        el.hidden = !isTarget;
      });

      this.currentStep = resolvedIndex;
      this.saveData();
      this.updateProgress();
      this.updateLastDoseHeading();
      this.restoreMeasurementFields();
      this.restoreSelectorGroups();
      if (stepName === 'medication-choice') {
        this.updateMedicationChoiceOptions();
      }
      if (stepName === 'next-dose-preference') {
        this.updateNextDosePreferenceOptions();
        this.maybeShowEnhancedSwitchInfo();
      }
      if (stepName === 'medication-supply') {
        this.updateMedicationSupplyDetail();
      }
      if (stepName === 'delivery-accessories') {
        this.updateDeliveryAccessoriesVisibility();
      }
      if (stepName === 'optional-extras') {
        this.updateOptionalExtrasUI();
      }
      if (stepName === 'delivery-date') {
        this.renderDeliveryCalendar();
      }
      this.updateContinueState();
      this.hideComingSoonNotice();

      if (animate !== false) {
        this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    goBack() {
      if (this.currentStep <= 0) return;
      const prev = this.resolveAvailableStepIndex(this.currentStep - 1, -1);
      if (prev < this.currentStep) this.goToStep(prev);
    }

    async goForward() {
      if (!this.validateCurrentStep()) return;

      const dateConfirm = this.getDateConfirmationPayload();
      if (dateConfirm) {
        const confirmed = await this.promptDateConfirmation(dateConfirm);
        if (!confirmed) return;
      }

      const step = STEPS[this.currentStep];
      if (step === 'last-injection-date' && this.updateConsultationGapFlag()) {
        this.saveData();
        this.goToConsultationNeeded();
        return;
      }

      if (this.currentStep < STEPS.length - 1) {
        const next = this.resolveAvailableStepIndex(this.currentStep + 1, 1);
        if (next > this.currentStep) {
          this.goToStep(next);
          return;
        }
      }

      this.showComingSoonNotice();
    }

    goToAssessment() {
      this.saveData();
      const previewThemeId = getUrlParam('preview_theme_id');
      const params = new URLSearchParams();
      if (previewThemeId) params.set('preview_theme_id', previewThemeId);
      const email = this.getPatientEmail();
      if (email) params.set('email', email);
      params.set('from', 'wbq');
      const qs = params.toString();
      window.location.assign(qs ? `${ASSESSMENT_PAGE_PATH}?${qs}` : ASSESSMENT_PAGE_PATH);
    }

    showComingSoonNotice() {
      const activeStep = this.getActiveStepEl();
      let notice = activeStep?.querySelector('[data-wbq-coming-soon]');
      if (!notice) {
        notice = this.root.querySelector('[data-wbq-coming-soon]');
      }
      if (!notice) return;
      notice.textContent = 'More WBQ steps coming soon.';
      notice.hidden = false;
      notice.classList.add('is-visible');
    }

    hideComingSoonNotice() {
      this.root.querySelectorAll('[data-wbq-coming-soon]').forEach((notice) => {
        notice.hidden = true;
        notice.classList.remove('is-visible');
      });
    }

    applyEmailFromUrlParams() {
      const raw = getUrlParam('email') || getUrlParam('mail') || getUrlParam('email_address');
      const email = normalizeEmailParam(raw);
      if (!email || !isValidEmail(email)) return;
      this.data.email = email;
      this.saveData();
    }

    getPatientEmail() {
      return (this.data.email || '').trim().toLowerCase();
    }

    setLastTreatmentDisplay(productName, dose) {
      const wrap = this.root.querySelector('[data-wbq-last-treatment-wrap]');
      const textEl = this.root.querySelector('[data-wbq-last-treatment-text]');
      if (!wrap || !textEl) return;

      if (this.forceMedKey) {
        const forcedLabel = getForcedProductLabel(this.forceMedKey);
        const label = forcedLabel
          ? formatLastTreatmentLabel(forcedLabel, dose)
          : formatLastTreatmentLabel(productName, dose);
        wrap.hidden = false;
        if (label) textEl.textContent = label;
        return;
      }

      const label = formatLastTreatmentLabel(productName, dose);
      wrap.hidden = false;
      if (label) textEl.textContent = label;
    }

    setLastTreatmentMessage(message) {
      const wrap = this.root.querySelector('[data-wbq-last-treatment-wrap]');
      const textEl = this.root.querySelector('[data-wbq-last-treatment-text]');
      if (!wrap || !textEl) return;
      wrap.hidden = false;
      textEl.textContent = message;
    }

    async loadLastTreatment() {
      const email = this.getPatientEmail();
      if (!email) {
        this.setLastTreatmentMessage('Your last treatment will appear here when we can identify your account.');
        return;
      }

      const cached = extractLastTreatment({
        product_purchased: this.data.productPurchased,
        last_product_name: this.data.lastProductName,
        last_product_dose: this.data.lastProductDose
      });
      if (cached.productName || cached.dose) {
        this.setLastTreatmentDisplay(cached.productName, cached.dose);
        this.ensureCurrentStepAvailable();
      } else {
        this.setLastTreatmentMessage('Loading your last treatment…');
      }

      try {
        const headers = { Accept: 'application/json' };
        if (this.apiTenant) headers['X-Tenant'] = this.apiTenant;

        const response = await fetch(
          `${this.quizPlpApiUrl}?email=${encodeURIComponent(email)}`,
          { headers, credentials: 'omit' }
        );
        if (!response.ok) {
          if (!(cached.productName || cached.dose)) {
            this.setLastTreatmentMessage('We could not load your last treatment right now.');
          }
          return;
        }

        const payload = await response.json();
        const patient = parseQuizPlpResponse(payload);
        if (!patient) {
          if (!(cached.productName || cached.dose)) {
            this.setLastTreatmentMessage('We could not load your last treatment right now.');
          }
          return;
        }

        const { productName, dose } = extractLastTreatment(patient);
        if (productName || dose) {
          if (this.forceMedKey) {
            const forcedLabel = getForcedProductLabel(this.forceMedKey);
            this.data.productPurchased = forcedLabel;
            this.data.lastProductName = forcedLabel;
            this.data.lastProductDose = resolveLastProductDose(
              patient.last_product_dose || dose || '',
              patient.product_purchased || forcedLabel,
              patient.last_product_name || forcedLabel
            );
            this.setLastTreatmentDisplay(forcedLabel, this.data.lastProductDose);
          } else {
            this.data.lastProductName = patient.last_product_name || productName;
            this.data.productPurchased = patient.product_purchased || productName || '';
            this.data.lastProductDose = resolveLastProductDose(
              patient.last_product_dose || dose,
              this.data.productPurchased,
              this.data.lastProductName
            );
            this.setLastTreatmentDisplay(productName, this.data.lastProductDose);
          }
          this.ensureCurrentStepAvailable();
        } else if (!(cached.productName || cached.dose)) {
          this.setLastTreatmentMessage('No previous treatment found for this account.');
        }

        this.data.quizPlpPatient = {
          patientFirstName: patient.patient_first_name || patient.first_name || '',
          lastName: patient.last_name || '',
          phoneNumber: patient.phone_number,
          height: patient.height,
          weight: patient.weight,
          lastDoseDate: patient.last_dose_date,
          lastPrescriptionStatus: patient.last_prescription_status
        };
        if (this.data.quizPlpPatient.patientFirstName) {
          this.data.firstName = this.data.quizPlpPatient.patientFirstName;
        }
        if (this.data.quizPlpPatient.lastName) {
          this.data.lastName = this.data.quizPlpPatient.lastName;
        }
        this.data.quizPlpProducts = patient.products || null;

        this.saveData();
        this.updateMedicationChoiceOptions();
        this.updateNextDosePreferenceOptions();
        this.updateRecommendedDose();
        this.saveData();
      } catch (_) {
        if (!(cached.productName || cached.dose)) {
          this.setLastTreatmentMessage('We could not load your last treatment right now.');
        }
      }
    }
  }

  function init() {
    document.querySelectorAll('[data-wbq-form]').forEach((root) => {
      if (root.dataset.initialized) return;
      root.dataset.initialized = 'true';
      new WbqForm(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

  window.SlimmrWbq = {
    clearSession: clearWbqSession,
    storageKey: STORAGE_KEY
  };
})();
